import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import useIsInappWebview from "hooks/useIsInappWebview";
import FullPageContainer from "pages/container/FullPageContainer";
import { theme } from "styles/theme";
import { AddIcon } from "static/image/common/IconAssets";
import { TrashIconSvg } from "static/image/common/SvgAssets";
import {
  createTaskEditorBlock,
  createTaskEditorButtonGroupOption,
  createTaskEditorLine,
  getTaskEditorBlockTypeLabel,
  getTaskEditorCellTypeLabel,
  getTaskEditorTypographyStyleLabel,
  isTaskEditorLastCardBlock,
  isTaskEditorNestedBlockType,
  reorderBlocks,
  reorderLines,
  TASK_EDITOR_EDITABLE_BLOCK_TYPES,
  TASK_EDITOR_EDITABLE_CELL_TYPES,
  TASK_EDITOR_LAST_CARD_TYPE,
  TASK_EDITOR_TYPOGRAPHY_STYLES,
  TaskEditorBlock,
  TaskEditorBlockType,
  TaskEditorButtonGroupOption,
  TaskEditorCellType,
  TaskEditorLine,
  TaskEditorState,
  TaskEditorTypographyStyle,
} from "./taskEditorUtils";
import {
  normalizeTaskEditorState,
  PublishedTaskEditorTask,
} from "./taskEditorRuntime";
import { buildTaskEditorListSections } from "./taskEditorListUtils";
import TaskEditorLivePreview from "./TaskEditorLivePreview";
import TaskEditorAIGenerateModal from "./TaskEditorAIGenerateModal";
import TaskEditorAIPreviewModal from "./TaskEditorAIPreviewModal";
import TaskEditorAIStatusBanner from "./TaskEditorAIStatusBanner";
import { useTaskEditorAIGenerate } from "./useTaskEditorAIGenerate";
import useTaskEditorStore from "hooks/useTaskEditorStore";

const panelHeaderHeight = "var(--StickyHeader-height)";
const taskListPanelWidth = "220px";
const taskListCollapsedPanelWidth = "84px";
const taskPropertyPanelWidth = "280px";
const taskListLoadingContainerHeight = "100%";
const panelScrollbarSx = {
  minHeight: 0,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
    opacity: 0,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&:hover::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,0.3)",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "rgba(0,0,0,0.4)",
  },
} as const;

const taskEditorBlockCardSx = {
  position: "relative",
  p: { xs: "16px", md: "20px" },
  borderRadius: "18px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
  transition:
    "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
} as const;

const taskEditorLineCardSx = {
  p: "14px",
  borderRadius: "16px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
  transition:
    "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
} as const;

const taskEditorLineIndexLabelSx = {
  color: theme.vars.palette.Content.Strong,
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
} as const;

const taskEditorBlockDropZoneSx = {
  position: "relative",
  height: "16px",
  mx: "8px",
  borderRadius: "12px",
  transition: "height 0.2s ease, opacity 0.2s ease, transform 0.2s ease",
} as const;

const taskEditorLineDropZoneSx = {
  ...taskEditorBlockDropZoneSx,
  height: "12px",
  mx: 0,
} as const;

const taskEditorAddBlockButtonWrapSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  py: 1,
} as const;

const taskEditorAddBlockButtonSx = {
  minWidth: "34px",
  width: "34px",
  height: "34px",
  borderRadius: "999px",
  border: "1px dashed",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  color: theme.vars.palette.Content.Weak,
  "&:hover": {
    borderColor: theme.vars.palette.Border.Strong,
    backgroundColor: theme.vars.palette.Background.Level2,
  },
} as const;

type TaskEditorDeleteTarget =
  | { type: "block"; blockId: string }
  | { type: "line"; blockId: string; lineId: string };

type TaskEditorBlockDropPreview = {
  insertionIndex: number;
};

type TaskEditorLineDragData = {
  blockId: string;
  lineId: string;
};

type TaskEditorLineDropPreview = {
  blockId: string;
  insertionIndex: number;
};

function convertTaskEditorImageFileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      const result = fileReader.result;

      if (typeof result !== "string") {
        reject(new Error("이미지 파일을 읽지 못했습니다."));
        return;
      }

      resolve(result);
    };

    fileReader.onerror = () => {
      reject(new Error("이미지 파일을 읽지 못했습니다."));
    };

    fileReader.readAsDataURL(file);
  });
}

function isTaskEditorLocalImageDataUrl(value: string) {
  return value.trim().startsWith("data:image/");
}

function normalizeTaskEditorButtonGroupOption(
  option: unknown
): TaskEditorButtonGroupOption {
  if (typeof option === "string") {
    return createTaskEditorButtonGroupOption({
      text: option,
    });
  }

  if (!option || typeof option !== "object") {
    return createTaskEditorButtonGroupOption();
  }

  const candidate = option as {
    text?: unknown;
    showFromId?: unknown;
    showToId?: unknown;
    restartFromId?: unknown;
    hideFromId?: unknown;
    isCorrect?: unknown;
  };

  return createTaskEditorButtonGroupOption({
    text: typeof candidate.text === "string" ? candidate.text : "",
    ...(typeof candidate.showFromId === "string"
      ? { showFromId: candidate.showFromId }
      : {}),
    ...(typeof candidate.showToId === "string"
      ? { showToId: candidate.showToId }
      : {}),
    ...(typeof candidate.restartFromId === "string"
      ? { restartFromId: candidate.restartFromId }
      : {}),
    ...(typeof candidate.hideFromId === "string"
      ? { hideFromId: candidate.hideFromId }
      : {}),
    ...(candidate.isCorrect === true ? { isCorrect: true } : {}),
  });
}

function getTaskEditorButtonGroupOptions(
  options: TaskEditorButtonGroupOption[] | undefined
) {
  if (!Array.isArray(options) || options.length === 0) {
    return [
      createTaskEditorButtonGroupOption(),
      createTaskEditorButtonGroupOption(),
    ];
  }

  return options.map((option) => normalizeTaskEditorButtonGroupOption(option));
}

type TaskEditorChoiceCellType = "ButtonGroup" | "ButtonSelection" | "Quiz";

function isTaskEditorChoiceCellType(
  type: TaskEditorCellType | TaskEditorLine["type"] | undefined
): type is TaskEditorChoiceCellType {
  return (
    type === "ButtonGroup" || type === "ButtonSelection" || type === "Quiz"
  );
}

const isTaskEditorUndoShortcut = (event: KeyboardEvent) => {
  if (event.altKey || event.shiftKey || event.isComposing) {
    return false;
  }

  return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z";
};

function getTaskEditorBlockReorderIndex(
  blocks: TaskEditorBlock[],
  sourceBlockId: string,
  preview: TaskEditorBlockDropPreview
) {
  const sourceIndex = blocks.findIndex((block) => block.id === sourceBlockId);
  const editableBlocks = blocks.filter(
    (block) => !isTaskEditorLastCardBlock(block)
  );
  const sourceEditableIndex = editableBlocks.findIndex(
    (block) => block.id === sourceBlockId
  );

  if (
    sourceIndex < 0 ||
    sourceEditableIndex < 0 ||
    isTaskEditorLastCardBlock(blocks[sourceIndex])
  ) {
    return null;
  }

  const nextIndex =
    preview.insertionIndex > sourceEditableIndex
      ? preview.insertionIndex - 1
      : preview.insertionIndex;

  return Math.max(0, Math.min(editableBlocks.length - 1, nextIndex));
}

function isSameTaskEditorBlockDropPreview(
  currentPreview: TaskEditorBlockDropPreview | null,
  nextPreview: TaskEditorBlockDropPreview
) {
  return currentPreview?.insertionIndex === nextPreview.insertionIndex;
}

function getTaskEditorLineReorderIndex(
  lines: TaskEditorLine[],
  sourceLineId: string,
  preview: TaskEditorLineDropPreview
) {
  const sourceIndex = lines.findIndex((line) => line.id === sourceLineId);

  if (sourceIndex < 0) {
    return null;
  }

  const nextIndex =
    preview.insertionIndex > sourceIndex
      ? preview.insertionIndex - 1
      : preview.insertionIndex;

  return Math.max(0, Math.min(lines.length - 1, nextIndex));
}

function isSameTaskEditorLineDropPreview(
  currentPreview: TaskEditorLineDropPreview | null,
  nextPreview: TaskEditorLineDropPreview
) {
  return (
    currentPreview?.blockId === nextPreview.blockId &&
    currentPreview?.insertionIndex === nextPreview.insertionIndex
  );
}

function getTaskEditorButtonGroupBranchTargetBlocks(
  blocks: TaskEditorBlock[],
  sourceBlockId: string
) {
  const sourceIndex = blocks.findIndex((block) => block.id === sourceBlockId);

  if (sourceIndex < 0) {
    return [];
  }

  return blocks
    .map((block, index) => ({
      block,
      index,
    }))
    .filter(
      ({ block, index }) =>
        !isTaskEditorLastCardBlock(block) &&
        index > sourceIndex &&
        block.id !== sourceBlockId
    );
}

function parseTaskEditorLineDragData(rawData: string) {
  if (!rawData) {
    return null;
  }

  try {
    const parsedData = JSON.parse(rawData) as Partial<TaskEditorLineDragData>;

    if (
      typeof parsedData.blockId !== "string" ||
      typeof parsedData.lineId !== "string"
    ) {
      return null;
    }

    return {
      blockId: parsedData.blockId,
      lineId: parsedData.lineId,
    };
  } catch {
    return null;
  }
}

const taskEditorDeleteTargetLabelMap = {
  block: "블록",
  line: "컴포넌트",
} as const;

const taskEditorSelectSx = {
  minHeight: "34px",
  px: 1.5,
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "0 8px 24px -16px rgba(0, 0, 0, 0.2)",
  color: theme.vars.palette.Content.Strong,
  fontSize: "12px",
  fontWeight: 700,
  "--Select-indicatorColor": theme.vars.palette.Content.Weak,
  "--Select-placeholderOpacity": 1,
  "--Select-decoratorColor": theme.vars.palette.Content.Weak,
  transition:
    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    borderColor: theme.vars.palette.Border.Weak,
    backgroundColor: theme.vars.palette.Surface.Forward,
    transform: "translateY(-1px)",
  },
  "&:focus-within": {
    borderColor: theme.vars.palette.primary.soft,
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.12)",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.vars.palette.Background.Level1,
    color: theme.vars.palette.Content.Weak,
    boxShadow: "none",
  },
} as const;

const taskEditorSelectButtonSx = {
  pl: 1.5,
  pr: 1.25,
} as const;

const taskEditorSelectListboxSx = {
  p: 0.5,
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  borderRadius: "12px",
  boxShadow:
    "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
  "--List-gap": "4px",
  "--ListItem-radius": "10px",
  "& .MuiOption-root": {
    minHeight: "40px",
    px: 1.25,
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 600,
    color: theme.vars.palette.Content.Strong,
  },
  "& .MuiOption-root:hover": {
    backgroundColor: theme.vars.palette.Background.Level1,
  },
  "& .MuiOption-root[aria-selected='true']": {
    backgroundColor: theme.vars.palette.primary.soft,
    color: theme.vars.palette.primary.solid,
  },
} as const;

const taskEditorCompactFieldLabelSx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: 1.3,
} as const;

const taskEditorButtonGroupEditorSx = {
  display: "grid",
  gap: 1,
  minWidth: 0,
  gridTemplateColumns: {
    xs: "1fr",
    md: "120px minmax(0, 1fr)",
  },
  alignItems: "start",
} as const;

const taskEditorCompactFieldSx = {
  minWidth: 0,
} as const;

const taskEditorButtonGroupOptionListSx = {
  display: "grid",
  gap: 0.75,
  minWidth: 0,
} as const;

const taskEditorButtonGroupGuideTriggerSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
} as const;

const taskEditorButtonGroupGuideIconSx = {
  minWidth: "20px",
  minHeight: "20px",
  width: "20px",
  height: "20px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  color: theme.vars.palette.Content.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  "&:hover": {
    backgroundColor: theme.vars.palette.Background.Level2,
  },
} as const;

const taskEditorButtonGroupGuideTooltipSx = {
  whiteSpace: "pre-line",
  fontSize: "12px",
  lineHeight: 1.5,
  color: "#FFFFFF",
  maxWidth: "300px",
} as const;

const taskEditorButtonGroupGuideTooltipSlotSx = {
  backgroundColor: "rgba(15, 23, 42, 0.96)",
  color: "#FFFFFF",
  boxShadow: "0 10px 28px rgba(2, 6, 23, 0.45)",
  borderRadius: "10px",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  px: 1.25,
  py: 1,
  maxWidth: "340px",
} as const;

const taskEditorButtonGroupGuideTooltipArrowSx = {
  "&::before": {
    borderTopColor: "rgba(15, 23, 42, 0.96)",
    borderRightColor: "rgba(15, 23, 42, 0.96)",
    boxShadow: "none",
  },
} as const;

const TASK_EDITOR_BUTTON_GROUP_GUIDE_TEXT = [
  "시작 블록: 이 버튼 옵션에서 처음 보여줄 블록입니다.",
  "종료 블록: 이 버튼 옵션에서 마지막으로 보여줄 블록입니다.",
  "다시 시작 블록: 종료 후 이어서 이동할 블록입니다.",
  "예: A(4~7, 다시 시작 12), B(8~10, 다시 시작 12), C(11, 다시 시작 12)",
].join("\n");

const taskEditorButtonGroupOptionRowSx = {
  display: "grid",
  gap: 0.5,
  minWidth: 0,
} as const;

const taskEditorButtonGroupBranchControlsSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(3, minmax(0, 1fr))",
  },
  gap: 0.5,
  minWidth: 0,
} as const;

const taskEditorButtonGroupBranchFieldSx = {
  minWidth: 0,
  display: "grid",
  gap: 0.25,
} as const;

const taskEditorButtonGroupBranchFieldLabelSx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: 1.2,
} as const;

const taskEditorButtonGroupSummarySx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "10px",
  lineHeight: 1.35,
  px: 0.25,
} as const;

const taskEditorButtonGroupOptionChipSx = {
  minHeight: "32px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  px: 0.75,
  py: 0.25,
} as const;

const taskEditorButtonGroupOptionInputSx = {
  minWidth: 0,
  width: "120px",
  "--Input-radius": "999px",
  "--Input-minHeight": "24px",
  "--Input-paddingInline": "8px",
  "--Input-decoratorChildHeight": "16px",
  "& .MuiInput-input": {
    fontSize: "12px",
    fontWeight: 600,
  },
} as const;

const taskEditorButtonGroupBranchSelectSx = {
  ...taskEditorSelectSx,
  minHeight: "30px",
  px: 1,
  fontSize: "11px",
  fontWeight: 600,
} as const;

const taskEditorButtonGroupAddButtonSx = {
  minHeight: "32px",
  borderRadius: "10px",
  px: 1.25,
} as const;

const taskEditorImagePreviewSx = {
  width: "100%",
  maxHeight: "220px",
  objectFit: "contain",
  borderRadius: "10px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
} as const;

const renderMetaFieldLabel = (label: string, isRequired = false) => (
  <Typography
    variant="Caption"
    sx={{ color: theme.vars.palette.Content.Strong, fontWeight: 700 }}
  >
    {label}
    {isRequired && (
      <Box component="span" sx={{ color: "danger.500", ml: "2px" }}>
        *
      </Box>
    )}
  </Typography>
);

const formatVersionTimestamp = (value: string) => {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const renderPanelHeader = (
  title: string,
  backgroundColor: string,
  action?: React.ReactNode
) => (
  <Box
    sx={{
      width: "100%",
      minHeight: panelHeaderHeight,
      height: panelHeaderHeight,
      backgroundColor,
      px: "20px",
      flexShrink: 0,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ height: "100%" }}
    >
      <Typography
        variant="Body"
        sx={{
          color: theme.vars.palette.Content.Strong,
          fontWeight: 700,
        }}
      >
        {title}
      </Typography>
      {action ?? <Box sx={{ width: "36px", flexShrink: 0 }} />}
    </Stack>
  </Box>
);

const cloneTaskEditorState = (state: TaskEditorState): TaskEditorState => ({
  taskId: state.taskId,
  title: state.title,
  setNumber: state.setNumber,
  isKeyActivity: state.isKeyActivity,
  blocks: state.blocks.map((block) => ({
    ...block,
    lines: block.lines?.map((line) => ({
      ...line,
      ...(isTaskEditorChoiceCellType(line.type)
        ? {
            options: getTaskEditorButtonGroupOptions(line.options).map(
              (option) => normalizeTaskEditorButtonGroupOption(option)
            ),
          }
        : {}),
      ...(line.type === "Quiz"
        ? {
            incorrectMessage: line.incorrectMessage ?? "",
          }
        : {}),
      ...(line.type === "ButtonSelection"
        ? {
            allowMultipleSelection: line.allowMultipleSelection !== false,
          }
        : {}),
    })),
  })),
});

const createEmptyTaskEditorState = (): TaskEditorState => ({
  taskId: "",
  title: "",
  setNumber: undefined,
  isKeyActivity: false,
  blocks: [
    createTaskEditorBlock("Instruction"),
    createTaskEditorBlock(TASK_EDITOR_LAST_CARD_TYPE),
  ],
});

const isMeaningfulTaskEditorState = (state: TaskEditorState) => {
  const normalizedState = normalizeTaskEditorState(state);
  const editableBlocks = normalizedState.blocks.filter(
    (block) => !isTaskEditorLastCardBlock(block)
  );

  return (
    Boolean(normalizedState.taskId) ||
    Boolean(normalizedState.title) ||
    normalizedState.setNumber !== undefined ||
    normalizedState.isKeyActivity === true ||
    editableBlocks.length > 1 ||
    editableBlocks[0]?.type !== "Instruction" ||
    Boolean(editableBlocks[0]?.content)
  );
};

const getTaskEditorDraftComparableSignature = (
  targetTaskId: string,
  state: TaskEditorState
) =>
  JSON.stringify(
    normalizeTaskEditorState({
      taskId: targetTaskId.trim(),
      title: state.title,
      setNumber: state.setNumber,
      isKeyActivity: state.isKeyActivity,
      blocks: state.blocks,
    })
  );

const hasTaskEditorDraftChanged = (
  targetTaskId: string,
  nextState: TaskEditorState,
  baselineState: TaskEditorState
) =>
  getTaskEditorDraftComparableSignature(targetTaskId, nextState) !==
  getTaskEditorDraftComparableSignature(targetTaskId, baselineState);

const getDisplayValue = (value?: string | number) => {
  if (value === undefined || value === "") {
    return "미입력";
  }

  return `${value}`;
};

type TaskEditorContentStats = {
  editableBlockCount: number;
  totalLineCount: number;
  blockTypeSummary: string;
  lineTypeSummary: string;
  configuredButtonOptionCount: number;
  filledTypographyLineCount: number;
  textareaPlaceholderCount: number;
  filledImageCount: number;
};

const getTaskEditorBlockSignature = (block: TaskEditorBlock) =>
  JSON.stringify({
    type: block.type,
    content: block.content,
    lines: block.lines?.map((line) => ({
      type: line.type,
      content: line.content,
      options: line.options,
      typographyStyle: line.typographyStyle,
      textareaPlaceholder: line.textareaPlaceholder,
      incorrectMessage: line.incorrectMessage,
      allowMultipleSelection: line.allowMultipleSelection,
    })),
  });

const getTaskEditorCountSummary = (countMap: Map<string, number>) => {
  if (countMap.size === 0) {
    return "없음";
  }

  return Array.from(countMap.entries())
    .sort(([labelA], [labelB]) => labelA.localeCompare(labelB))
    .map(([label, count]) => `${label} ${count}개`)
    .join(", ");
};

const getTaskEditorContentStats = (
  blocks: TaskEditorBlock[]
): TaskEditorContentStats => {
  const editableBlocks = blocks.filter(
    (block) => !isTaskEditorLastCardBlock(block)
  );
  const blockTypeCountMap = new Map<string, number>();

  editableBlocks.forEach((block) => {
    const blockLabel = getTaskEditorBlockTypeLabel(block.type);

    blockTypeCountMap.set(
      blockLabel,
      (blockTypeCountMap.get(blockLabel) ?? 0) + 1
    );
  });

  const lines = editableBlocks.flatMap((block) =>
    isTaskEditorNestedBlockType(block.type) ? (block.lines ?? []) : []
  );
  const lineTypeCountMap = new Map<string, number>();
  let configuredButtonOptionCount = 0;
  let filledTypographyLineCount = 0;
  let textareaPlaceholderCount = 0;
  let filledImageCount = 0;

  lines.forEach((line) => {
    const lineLabel = getTaskEditorCellTypeLabel(line.type);

    lineTypeCountMap.set(lineLabel, (lineTypeCountMap.get(lineLabel) ?? 0) + 1);

    if (isTaskEditorChoiceCellType(line.type)) {
      configuredButtonOptionCount += getTaskEditorButtonGroupOptions(
        line.options
      ).filter((option) => {
        const hasText = option.text.trim().length > 0;
        const hasBranchRule =
          (option.showFromId ?? "").trim().length > 0 ||
          (option.showToId ?? "").trim().length > 0 ||
          (option.restartFromId ?? "").trim().length > 0 ||
          (option.hideFromId ?? "").trim().length > 0;
        const hasCorrectMark = option.isCorrect === true;

        return hasText || hasBranchRule || hasCorrectMark;
      }).length;
    }

    if (line.type === "Typography" && line.content.trim().length > 0) {
      filledTypographyLineCount += 1;
    }

    if (
      (line.type === "Textarea" || line.type === "Preset") &&
      (line.textareaPlaceholder ?? "").trim().length > 0
    ) {
      textareaPlaceholderCount += 1;
    }

    if (line.type === "Image" && line.content.trim().length > 0) {
      filledImageCount += 1;
    }
  });

  return {
    editableBlockCount: editableBlocks.length,
    totalLineCount: lines.length,
    blockTypeSummary: getTaskEditorCountSummary(blockTypeCountMap),
    lineTypeSummary: getTaskEditorCountSummary(lineTypeCountMap),
    configuredButtonOptionCount,
    filledTypographyLineCount,
    textareaPlaceholderCount,
    filledImageCount,
  };
};

const getBlockChangeSummary = (
  previousBlocks: TaskEditorBlock[],
  nextBlocks: TaskEditorBlock[]
) => {
  const previousStats = getTaskEditorContentStats(previousBlocks);
  const nextStats = getTaskEditorContentStats(nextBlocks);
  const summaries: string[] = [];

  if (previousStats.editableBlockCount !== nextStats.editableBlockCount) {
    summaries.push(
      `편집 블록 수: ${previousStats.editableBlockCount}개 → ${nextStats.editableBlockCount}개`
    );
  }

  if (previousStats.blockTypeSummary !== nextStats.blockTypeSummary) {
    summaries.push(
      `블록 구성: ${previousStats.blockTypeSummary} → ${nextStats.blockTypeSummary}`
    );
  }

  if (previousStats.totalLineCount !== nextStats.totalLineCount) {
    summaries.push(
      `컴포넌트 수: ${previousStats.totalLineCount}개 → ${nextStats.totalLineCount}개`
    );
  }

  if (previousStats.lineTypeSummary !== nextStats.lineTypeSummary) {
    summaries.push(
      `컴포넌트 구성: ${previousStats.lineTypeSummary} → ${nextStats.lineTypeSummary}`
    );
  }

  if (
    previousStats.configuredButtonOptionCount !==
    nextStats.configuredButtonOptionCount
  ) {
    summaries.push(
      `선택지 옵션 설정 수: ${previousStats.configuredButtonOptionCount}개 → ${nextStats.configuredButtonOptionCount}개`
    );
  }

  if (
    previousStats.filledTypographyLineCount !==
    nextStats.filledTypographyLineCount
  ) {
    summaries.push(
      `내용이 입력된 텍스트 컴포넌트 수: ${previousStats.filledTypographyLineCount}개 → ${nextStats.filledTypographyLineCount}개`
    );
  }

  if (
    previousStats.textareaPlaceholderCount !==
    nextStats.textareaPlaceholderCount
  ) {
    summaries.push(
      `입력 안내(placeholder) 설정 수: ${previousStats.textareaPlaceholderCount}개 → ${nextStats.textareaPlaceholderCount}개`
    );
  }

  if (previousStats.filledImageCount !== nextStats.filledImageCount) {
    summaries.push(
      `이미지 컴포넌트 수: ${previousStats.filledImageCount}개 → ${nextStats.filledImageCount}개`
    );
  }

  const previousEditableBlockSignatures = previousBlocks
    .filter((block) => !isTaskEditorLastCardBlock(block))
    .map((block) => getTaskEditorBlockSignature(block));
  const nextEditableBlockSignatures = nextBlocks
    .filter((block) => !isTaskEditorLastCardBlock(block))
    .map((block) => getTaskEditorBlockSignature(block));
  const hasDifferentOrderOrContent =
    previousEditableBlockSignatures.join("||") !==
    nextEditableBlockSignatures.join("||");
  const hasSameBlockSet =
    previousEditableBlockSignatures.length ===
      nextEditableBlockSignatures.length &&
    [...previousEditableBlockSignatures].sort().join("||") ===
      [...nextEditableBlockSignatures].sort().join("||");

  if (hasDifferentOrderOrContent && hasSameBlockSet) {
    summaries.push("블록 순서가 변경되었습니다.");
  }

  const comparableBlockCount = Math.min(
    previousEditableBlockSignatures.length,
    nextEditableBlockSignatures.length
  );
  let changedComparableBlockCount = 0;

  for (let blockIndex = 0; blockIndex < comparableBlockCount; blockIndex += 1) {
    if (
      previousEditableBlockSignatures[blockIndex] !==
      nextEditableBlockSignatures[blockIndex]
    ) {
      changedComparableBlockCount += 1;
    }
  }

  if (changedComparableBlockCount > 0) {
    summaries.push(
      `동일 위치 기준으로 ${changedComparableBlockCount}개 블록의 세부 내용이 수정되었습니다.`
    );
  }

  const previousSignature = previousBlocks
    .map((block) => getTaskEditorBlockSignature(block))
    .join("||");
  const nextSignature = nextBlocks
    .map((block) => getTaskEditorBlockSignature(block))
    .join("||");

  if (summaries.length === 0 && previousSignature !== nextSignature) {
    summaries.push("활동지 내용 구성이 변경되었습니다.");
  }

  return summaries;
};

const getDeployChangeSummaries = (
  state: TaskEditorState,
  publishedTask?: PublishedTaskEditorTask
) => {
  const normalizedState = normalizeTaskEditorState(state);

  if (!publishedTask) {
    if (!isMeaningfulTaskEditorState(normalizedState)) {
      return [];
    }

    const changes = ["새 활동지가 배포됩니다. (기존 배포본 없음)"];

    changes.push(`활동지 ID: ${getDisplayValue(normalizedState.taskId)}`);
    changes.push(`활동지 제목: ${getDisplayValue(normalizedState.title)}`);
    changes.push(`세트 번호: ${getDisplayValue(normalizedState.setNumber)}`);
    changes.push(
      normalizedState.isKeyActivity
        ? "Key 활동지 여부: Key 활동지"
        : "Key 활동지 여부: 일반 활동지"
    );
    changes.push(...getBlockChangeSummary([], normalizedState.blocks));

    return changes;
  }

  const normalizedPublishedTask = normalizeTaskEditorState(publishedTask);
  const changes: string[] = [];

  if (normalizedPublishedTask.taskId !== normalizedState.taskId) {
    changes.push(
      `활동지 ID: ${getDisplayValue(normalizedPublishedTask.taskId)} → ${getDisplayValue(
        normalizedState.taskId
      )}`
    );
  }

  if (normalizedPublishedTask.title !== normalizedState.title) {
    changes.push(
      `활동지 제목: ${getDisplayValue(normalizedPublishedTask.title)} → ${getDisplayValue(
        normalizedState.title
      )}`
    );
  }

  if (normalizedPublishedTask.setNumber !== normalizedState.setNumber) {
    changes.push(
      `세트 번호: ${getDisplayValue(normalizedPublishedTask.setNumber)} → ${getDisplayValue(
        normalizedState.setNumber
      )}`
    );
  }

  if (normalizedPublishedTask.isKeyActivity !== normalizedState.isKeyActivity) {
    changes.push(
      normalizedState.isKeyActivity
        ? "Key 활동지 여부: 일반 활동지 → Key 활동지"
        : "Key 활동지 여부: Key 활동지 → 일반 활동지"
    );
  }

  changes.push(
    ...getBlockChangeSummary(
      normalizedPublishedTask.blocks,
      normalizedState.blocks
    )
  );

  return changes;
};

export default function TaskEditorPage() {
  const isInappWebview = useIsInappWebview();
  const [taskId, setTaskId] = useState("");
  const [title, setTitle] = useState("");
  const [setNumber, setSetNumber] = useState<number | undefined>(undefined);
  const [isKeyActivity, setIsKeyActivity] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(
    undefined
  );
  const [highlightedTaskId, setHighlightedTaskId] = useState<
    string | undefined
  >(undefined);
  const [undoHistory, setUndoHistory] = useState<TaskEditorState[]>([]);
  const [hasHydratedDraft, setHasHydratedDraft] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTaskMetadataMenuOpen, setIsTaskMetadataMenuOpen] = useState(false);
  const [pendingDeleteTarget, setPendingDeleteTarget] =
    useState<TaskEditorDeleteTarget | null>(null);
  const textEditUndoGroupRef = useRef<string | null>(null);
  const textEditUndoResetTimerRef = useRef<number | undefined>(undefined);
  const draggedBlockIdRef = useRef<string | null>(null);
  const blockDropPreviewRef = useRef<TaskEditorBlockDropPreview | null>(null);
  const didReorderBlockDuringDragRef = useRef(false);
  const draggedLineRef = useRef<TaskEditorLineDragData | null>(null);
  const lineDropPreviewRef = useRef<TaskEditorLineDropPreview | null>(null);
  const didReorderLineDuringDragRef = useRef(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [blocks, setBlocks] = useState<TaskEditorBlock[]>(
    createEmptyTaskEditorState().blocks
  );
  const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
  const [blockDropPreview, setBlockDropPreview] =
    useState<TaskEditorBlockDropPreview | null>(null);
  const [draggedLine, setDraggedLine] = useState<TaskEditorLineDragData | null>(
    null
  );
  const [lineDropPreview, setLineDropPreview] =
    useState<TaskEditorLineDropPreview | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isDeletingTask, setIsDeletingTask] = useState(false);
  const [isAIGenerateModalOpen, setIsAIGenerateModalOpen] = useState(false);
  const [isAIPreviewModalOpen, setIsAIPreviewModalOpen] = useState(false);
  const {
    status: aiGenerateStatus,
    startGenerate: startAIGenerate,
    dismiss: dismissAIGenerate,
  } = useTaskEditorAIGenerate();
  const {
    publishedTasks: savedTasks,
    workingDraft,
    taskDraftsByTaskId,
    isStoreHydrated,
    isStoreLoading,
    hydrateTaskEditorStore,
    saveWorkingDraft,
    saveTaskDraft: persistTaskDraft,
    publishTask,
    removeTaskDraft: removePersistedTaskDraft,
    removeTask,
  } = useTaskEditorStore();
  const [isTaskListCollapsed, setIsTaskListCollapsed] = useState(false);
  const sidebarBackgroundColor = isInappWebview
    ? theme.vars.palette.Background.Level1
    : theme.vars.palette.Background.Level2;

  const normalizedTaskId = taskId.trim();
  const isCurrentTaskIdTaken = useMemo(() => {
    if (!normalizedTaskId) {
      return false;
    }

    return savedTasks.some(
      (task) =>
        task.taskId === normalizedTaskId && task.taskId !== selectedTaskId
    );
  }, [normalizedTaskId, savedTasks, selectedTaskId]);
  const currentEditorState = useMemo(
    () => ({
      taskId,
      title,
      setNumber,
      isKeyActivity,
      blocks,
    }),
    [blocks, isKeyActivity, setNumber, taskId, title]
  );
  const isTaskEditorActive = highlightedTaskId !== undefined || isCreatingTask;
  const isTaskEditorStoreHydrating = isStoreLoading && !hasHydratedDraft;
  const isTaskListLoading = isStoreLoading;
  const isTaskMetadataLoading = isTaskEditorStoreHydrating;
  const isTaskDraftLoading = isTaskEditorStoreHydrating;
  const displayedSavedTasks = useMemo(() => {
    const taskMap = new Map(
      savedTasks.map((savedTask) => [savedTask.taskId, savedTask] as const)
    );

    Object.entries(taskDraftsByTaskId).forEach(([draftTaskId, draftState]) => {
      const savedTask = taskMap.get(draftTaskId);

      if (!savedTask) {
        return;
      }

      const normalizedDraftState = normalizeTaskEditorState(draftState);

      taskMap.set(draftTaskId, {
        ...savedTask,
        title: normalizedDraftState.title || savedTask.title,
        setNumber: normalizedDraftState.setNumber,
        isKeyActivity: normalizedDraftState.isKeyActivity,
      });
    });

    if (isTaskEditorActive && selectedTaskId) {
      const selectedSavedTask = taskMap.get(selectedTaskId);

      if (selectedSavedTask) {
        const normalizedState = normalizeTaskEditorState(currentEditorState);

        taskMap.set(selectedTaskId, {
          ...selectedSavedTask,
          title: normalizedState.title || selectedSavedTask.title,
          setNumber: normalizedState.setNumber,
          isKeyActivity: normalizedState.isKeyActivity,
        });
      }
    }

    return Array.from(taskMap.values());
  }, [
    currentEditorState,
    isTaskEditorActive,
    savedTasks,
    selectedTaskId,
    taskDraftsByTaskId,
  ]);
  const taskListSections = useMemo(
    () => buildTaskEditorListSections(displayedSavedTasks),
    [displayedSavedTasks]
  );
  const selectedPublishedTask = useMemo(
    () =>
      selectedTaskId
        ? savedTasks.find((savedTask) => savedTask.taskId === selectedTaskId)
        : undefined,
    [savedTasks, selectedTaskId]
  );
  const deployChangeSummaries = useMemo(
    () => getDeployChangeSummaries(currentEditorState, selectedPublishedTask),
    [currentEditorState, selectedPublishedTask]
  );
  const deletableTaskId = useMemo(() => {
    if (selectedTaskId) {
      return selectedTaskId;
    }

    return savedTasks.some((savedTask) => savedTask.taskId === normalizedTaskId)
      ? normalizedTaskId
      : undefined;
  }, [normalizedTaskId, savedTasks, selectedTaskId]);
  const canDeploy =
    Boolean(normalizedTaskId) &&
    !isCurrentTaskIdTaken &&
    deployChangeSummaries.length > 0;
  const getExistingKeyActivityInSameSet = (
    targetSetNumber: number | undefined
  ): PublishedTaskEditorTask | undefined => {
    if (targetSetNumber === undefined) {
      return undefined;
    }

    return displayedSavedTasks.find(
      (task) =>
        task.setNumber === targetSetNumber &&
        task.isKeyActivity === true &&
        task.taskId !== selectedTaskId
    );
  };
  const existingKeyActivityInCurrentSet =
    getExistingKeyActivityInSameSet(setNumber);
  const isKeyActivityCheckboxDisabled =
    !isKeyActivity && Boolean(existingKeyActivityInCurrentSet);
  const pendingDeleteTargetLabel = pendingDeleteTarget
    ? taskEditorDeleteTargetLabelMap[pendingDeleteTarget.type]
    : undefined;
  const renderedBlocks = blocks;

  const resetTextEditUndoGrouping = () => {
    textEditUndoGroupRef.current = null;

    if (textEditUndoResetTimerRef.current !== undefined) {
      window.clearTimeout(textEditUndoResetTimerRef.current);
      textEditUndoResetTimerRef.current = undefined;
    }
  };

  const scheduleTextEditUndoGroupingReset = () => {
    if (textEditUndoResetTimerRef.current !== undefined) {
      window.clearTimeout(textEditUndoResetTimerRef.current);
    }

    textEditUndoResetTimerRef.current = window.setTimeout(() => {
      textEditUndoGroupRef.current = null;
      textEditUndoResetTimerRef.current = undefined;
    }, 500);
  };

  const clearBlockDragState = () => {
    draggedBlockIdRef.current = null;
    blockDropPreviewRef.current = null;
    didReorderBlockDuringDragRef.current = false;
    setDraggedBlockId(null);
    setBlockDropPreview(null);
  };

  const clearLineDragState = () => {
    draggedLineRef.current = null;
    lineDropPreviewRef.current = null;
    didReorderLineDuringDragRef.current = false;
    setDraggedLine(null);
    setLineDropPreview(null);
  };

  const handleCellTypeChange = (
    blockId: string,
    lineId: string,
    _cellId: string,
    type: TaskEditorCellType | null
  ) => {
    if (!type) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) => {
            if (line.id !== lineId) {
              return line;
            }

            return {
              ...createTaskEditorLine(type),
              id: line.id,
              content: line.content,
              ...(isTaskEditorChoiceCellType(type) &&
              isTaskEditorChoiceCellType(line.type)
                ? {
                    options: getTaskEditorButtonGroupOptions(line.options).map(
                      (option) => normalizeTaskEditorButtonGroupOption(option)
                    ),
                  }
                : {}),
              ...(type === "Quiz" && line.type === "Quiz"
                ? {
                    incorrectMessage: line.incorrectMessage ?? "",
                  }
                : {}),
              ...(type === "ButtonSelection" && line.type === "ButtonSelection"
                ? {
                    allowMultipleSelection:
                      line.allowMultipleSelection !== false,
                  }
                : {}),
              ...((type === "Textarea" || type === "Preset") &&
              (line.type === "Textarea" || line.type === "Preset")
                ? {
                    textareaPlaceholder: line.textareaPlaceholder ?? "",
                  }
                : {}),
            };
          }),
        };
      })
    );
  };

  useEffect(() => {
    if (hasHydratedDraft) {
      return;
    }

    let cancelled = false;

    const hydrate = async () => {
      try {
        const hydratedStore = isStoreHydrated
          ? {
              publishedTasks: savedTasks,
              workingDraft,
            }
          : await hydrateTaskEditorStore();

        if (cancelled) {
          return;
        }

        const draft = hydratedStore.workingDraft;
        if (!draft) {
          setHasHydratedDraft(true);
          return;
        }

        setTaskId(draft.taskId);
        setTitle(draft.title);
        setSetNumber(draft.setNumber);
        setIsKeyActivity(draft.isKeyActivity === true);
        setBlocks(normalizeTaskEditorState(draft).blocks);

        if (draft.taskId) {
          const matchedTask = hydratedStore.publishedTasks.find(
            (task) => task.taskId === draft.taskId
          );

          if (matchedTask) {
            setSelectedTaskId(matchedTask.taskId);
            setHighlightedTaskId(matchedTask.taskId);
          }
        }

        if (isMeaningfulTaskEditorState(draft)) {
          setIsCreatingTask(true);
        }

        setHasHydratedDraft(true);
      } catch (error) {
        if (!cancelled) {
          setHasHydratedDraft(false);
        }
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [
    hasHydratedDraft,
    hydrateTaskEditorStore,
    isStoreHydrated,
    savedTasks,
    workingDraft,
  ]);

  useEffect(() => {
    if (!hasHydratedDraft) {
      return;
    }

    if (
      !isTaskEditorActive &&
      !isMeaningfulTaskEditorState(currentEditorState)
    ) {
      return;
    }

    saveWorkingDraft({
      taskId,
      title,
      setNumber,
      isKeyActivity,
      blocks,
    }).catch(() => {});
  }, [
    blocks,
    currentEditorState,
    hasHydratedDraft,
    isKeyActivity,
    isTaskEditorActive,
    saveWorkingDraft,
    setNumber,
    taskId,
    title,
  ]);

  useEffect(() => {
    if (!hasHydratedDraft || !selectedTaskId || !isTaskEditorActive) {
      return;
    }

    if (!isMeaningfulTaskEditorState(currentEditorState)) {
      return;
    }

    const normalizedSelectedTaskId = selectedTaskId.trim();
    if (!normalizedSelectedTaskId) {
      return;
    }

    const baselineTaskState =
      taskDraftsByTaskId[normalizedSelectedTaskId] ??
      savedTasks.find((task) => task.taskId === normalizedSelectedTaskId);

    if (
      baselineTaskState &&
      !hasTaskEditorDraftChanged(
        normalizedSelectedTaskId,
        currentEditorState,
        baselineTaskState
      )
    ) {
      return;
    }

    persistTaskDraft(normalizedSelectedTaskId, currentEditorState).catch(
      () => {}
    );
  }, [
    currentEditorState,
    hasHydratedDraft,
    isTaskEditorActive,
    persistTaskDraft,
    savedTasks,
    selectedTaskId,
    taskDraftsByTaskId,
  ]);

  useEffect(() => {
    return () => {
      if (textEditUndoResetTimerRef.current !== undefined) {
        window.clearTimeout(textEditUndoResetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handlePointerDown = () => {
      setIsTaskMetadataMenuOpen(false);
    };

    window.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (!isTaskEditorActive || !deletableTaskId) {
      setIsTaskMetadataMenuOpen(false);
    }
  }, [deletableTaskId, isTaskEditorActive]);

  const applyEditorState = (state: TaskEditorState) => {
    const nextState = normalizeTaskEditorState(cloneTaskEditorState(state));

    setTaskId(nextState.taskId);
    setTitle(nextState.title);
    setSetNumber(nextState.setNumber);
    setIsKeyActivity(nextState.isKeyActivity === true);
    setBlocks(
      nextState.blocks.length ? nextState.blocks : [createTaskEditorBlock()]
    );
  };

  const cacheTaskDraft = (
    targetTaskId: string,
    state: TaskEditorState = currentEditorState
  ) => {
    const normalizedTargetTaskId = targetTaskId.trim();
    if (!normalizedTargetTaskId) {
      return;
    }

    const baselineTaskState =
      taskDraftsByTaskId[normalizedTargetTaskId] ??
      savedTasks.find((task) => task.taskId === normalizedTargetTaskId);

    if (
      baselineTaskState &&
      !hasTaskEditorDraftChanged(
        normalizedTargetTaskId,
        state,
        baselineTaskState
      )
    ) {
      return;
    }

    persistTaskDraft(normalizedTargetTaskId, state).catch(() => {});
  };

  const removeTaskDraft = (targetTaskId: string) => {
    const normalizedTargetTaskId = targetTaskId.trim();
    if (!normalizedTargetTaskId) {
      return;
    }

    removePersistedTaskDraft(normalizedTargetTaskId).catch(() => {});
  };

  const pushUndoSnapshot = (state: TaskEditorState = currentEditorState) => {
    const nextSnapshot = cloneTaskEditorState(state);
    const nextSerializedSnapshot = JSON.stringify(nextSnapshot);

    setUndoHistory((currentHistory) => {
      if (
        currentHistory.length > 0 &&
        JSON.stringify(currentHistory[currentHistory.length - 1]) ===
          nextSerializedSnapshot
      ) {
        return currentHistory;
      }

      return [...currentHistory.slice(-49), nextSnapshot];
    });
  };

  const pushUndoSnapshotForTextEdit = (
    groupKey: string,
    state: TaskEditorState = currentEditorState
  ) => {
    if (textEditUndoGroupRef.current !== groupKey) {
      pushUndoSnapshot(state);
      textEditUndoGroupRef.current = groupKey;
    }

    scheduleTextEditUndoGroupingReset();
  };

  const handleUndo = () => {
    const previousState = undoHistory[undoHistory.length - 1];

    if (!previousState) {
      return;
    }

    resetTextEditUndoGrouping();
    setUndoHistory((currentHistory) => currentHistory.slice(0, -1));
    applyEditorState(previousState);
    setStatusMessage("최근 변경을 되돌렸습니다.");
  };

  useEffect(() => {
    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (
        !isTaskEditorActive ||
        undoHistory.length === 0 ||
        event.defaultPrevented ||
        event.repeat ||
        !isTaskEditorUndoShortcut(event)
      ) {
        return;
      }

      event.preventDefault();
      handleUndo();
    };

    window.addEventListener("keydown", handleWindowKeyDown);

    return () => {
      window.removeEventListener("keydown", handleWindowKeyDown);
    };
  }, [handleUndo, isTaskEditorActive, undoHistory.length]);

  const handleTaskIdChange = (nextTaskId: string) => {
    if (nextTaskId === taskId) {
      return;
    }

    pushUndoSnapshotForTextEdit("task-id");
    setTaskId(nextTaskId);
  };

  const handleTitleChange = (nextTitle: string) => {
    if (nextTitle === title) {
      return;
    }

    pushUndoSnapshotForTextEdit("task-title");
    setTitle(nextTitle);
  };

  const handleSetNumberChange = (nextValue: string) => {
    const parsedValue = nextValue
      ? Number.isFinite(Number(nextValue)) && Number(nextValue) > 0
        ? Number(nextValue)
        : undefined
      : undefined;

    if (parsedValue === setNumber) {
      return;
    }

    pushUndoSnapshotForTextEdit("set-number");
    setSetNumber(parsedValue);

    if (isKeyActivity && getExistingKeyActivityInSameSet(parsedValue)) {
      setIsKeyActivity(false);
    }
  };

  const handleKeyActivityChange = (checked: boolean) => {
    if (checked === isKeyActivity) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setIsKeyActivity(checked);
  };

  const handleBlockTypeChange = (
    blockId: string,
    nextType: TaskEditorBlockType
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);

    if (
      !currentBlock ||
      isTaskEditorLastCardBlock(currentBlock) ||
      nextType === TASK_EDITOR_LAST_CARD_TYPE ||
      currentBlock.type === nextType
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) =>
        block.id === blockId
          ? {
              ...block,
              type: nextType,
              ...(isTaskEditorNestedBlockType(nextType)
                ? {
                    lines:
                      block.lines && block.lines.length > 0
                        ? block.lines
                        : [createTaskEditorLine()],
                  }
                : {}),
            }
          : block
      )
    );
  };

  const handleBlockContentChange = (blockId: string, content: string) => {
    const currentBlock = blocks.find((block) => block.id === blockId);

    if (
      !currentBlock ||
      isTaskEditorLastCardBlock(currentBlock) ||
      currentBlock.content === content
    ) {
      return;
    }

    pushUndoSnapshotForTextEdit(`block-content:${blockId}`);
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) =>
        block.id === blockId ? { ...block, content } : block
      )
    );
  };

  const handleAddBlock = (
    type: TaskEditorBlockType,
    insertionIndex?: number
  ) => {
    if (type === TASK_EDITOR_LAST_CARD_TYPE) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) => {
      const nextBlocks = [...currentBlocks];
      const editableBlockIndexes = currentBlocks.reduce<number[]>(
        (indexes, block, index) => {
          if (!isTaskEditorLastCardBlock(block)) {
            indexes.push(index);
          }

          return indexes;
        },
        []
      );
      const normalizedInsertionIndex =
        typeof insertionIndex === "number" && Number.isFinite(insertionIndex)
          ? Math.max(0, Math.min(insertionIndex, editableBlockIndexes.length))
          : editableBlockIndexes.length;
      const targetArrayIndex =
        normalizedInsertionIndex >= editableBlockIndexes.length
          ? (() => {
              const lastCardIndex = currentBlocks.findIndex(
                isTaskEditorLastCardBlock
              );
              return lastCardIndex === -1
                ? currentBlocks.length
                : lastCardIndex;
            })()
          : editableBlockIndexes[normalizedInsertionIndex];

      nextBlocks.splice(targetArrayIndex, 0, createTaskEditorBlock(type));
      return nextBlocks;
    });
  };

  const handleAddLine = (blockId: string) => {
    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) =>
        block.id === blockId && isTaskEditorNestedBlockType(block.type)
          ? {
              ...block,
              lines: [...(block.lines ?? []), createTaskEditorLine()],
            }
          : block
      )
    );
  };

  const handleRemoveLine = (blockId: string, lineId: string) => {
    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        const nextLines = (block.lines ?? []).filter(
          (line) => line.id !== lineId
        );

        return {
          ...block,
          lines: nextLines.length > 0 ? nextLines : [createTaskEditorLine()],
        };
      })
    );
  };

  const handleContentLineCellChange = (
    blockId: string,
    lineId: string,
    _cellId: string,
    content: string
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      !currentLine ||
      currentLine.content === content
    ) {
      return;
    }

    pushUndoSnapshotForTextEdit(`content-cell:${blockId}:${lineId}`);
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) =>
            line.id === lineId
              ? {
                  ...line,
                  content,
                }
              : line
          ),
        };
      })
    );
  };

  const handleButtonGroupOptionChange = (
    blockId: string,
    lineId: string,
    optionIndex: number,
    value: string
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
    const isChoiceLine = currentLine
      ? isTaskEditorChoiceCellType(currentLine.type)
      : false;
    const currentOptions = isChoiceLine
      ? getTaskEditorButtonGroupOptions(currentLine?.options)
      : undefined;

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      !isChoiceLine ||
      !currentOptions ||
      optionIndex < 0 ||
      optionIndex >= currentOptions.length ||
      currentOptions[optionIndex].text === value
    ) {
      return;
    }

    pushUndoSnapshotForTextEdit(
      `button-group-option:${blockId}:${lineId}:${optionIndex}`
    );
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) => {
            if (line.id !== lineId || !isTaskEditorChoiceCellType(line.type)) {
              return line;
            }

            const nextOptions = getTaskEditorButtonGroupOptions(
              line.options
            ).map((option) => normalizeTaskEditorButtonGroupOption(option));
            nextOptions[optionIndex] = createTaskEditorButtonGroupOption({
              ...nextOptions[optionIndex],
              text: value,
            });

            return {
              ...line,
              options: nextOptions,
            };
          }),
        };
      })
    );
  };

  const handleButtonGroupOptionBranchChange = (
    blockId: string,
    lineId: string,
    optionIndex: number,
    key: "showFromId" | "showToId" | "restartFromId" | "hideFromId",
    value: string
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
    const currentOptions =
      currentLine?.type === "ButtonGroup"
        ? getTaskEditorButtonGroupOptions(currentLine.options)
        : undefined;
    const normalizedValue = value.trim();
    const requestedValue =
      normalizedValue.length > 0 ? normalizedValue : undefined;
    const branchTargetBlocks = getTaskEditorButtonGroupBranchTargetBlocks(
      blocks,
      blockId
    );
    const isValidBranchValue =
      !requestedValue ||
      branchTargetBlocks.some(({ block }) => block.id === requestedValue);
    const nextBranchValue = isValidBranchValue ? requestedValue : undefined;
    const currentOptionValue =
      currentOptions && optionIndex >= 0 && optionIndex < currentOptions.length
        ? currentOptions[optionIndex][key]
        : undefined;

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      currentLine?.type !== "ButtonGroup" ||
      !currentOptions ||
      optionIndex < 0 ||
      optionIndex >= currentOptions.length ||
      currentOptionValue === nextBranchValue
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) => {
            if (line.id !== lineId || line.type !== "ButtonGroup") {
              return line;
            }

            const nextOptions = getTaskEditorButtonGroupOptions(
              line.options
            ).map((option) => normalizeTaskEditorButtonGroupOption(option));
            const targetOption = nextOptions[optionIndex];
            nextOptions[optionIndex] = createTaskEditorButtonGroupOption({
              ...targetOption,
              [key]: nextBranchValue,
            });

            return {
              ...line,
              options: nextOptions,
            };
          }),
        };
      })
    );
  };

  const handleAddButtonGroupOption = (blockId: string, lineId: string) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      !isTaskEditorChoiceCellType(currentLine?.type)
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) => {
            if (line.id !== lineId || !isTaskEditorChoiceCellType(line.type)) {
              return line;
            }

            return {
              ...line,
              options: [
                ...getTaskEditorButtonGroupOptions(line.options).map((option) =>
                  normalizeTaskEditorButtonGroupOption(option)
                ),
                createTaskEditorButtonGroupOption(),
              ],
            };
          }),
        };
      })
    );
  };

  const handleRemoveButtonGroupOption = (
    blockId: string,
    lineId: string,
    optionIndex: number
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
    const isChoiceLine = currentLine
      ? isTaskEditorChoiceCellType(currentLine.type)
      : false;
    const currentOptions = isChoiceLine
      ? getTaskEditorButtonGroupOptions(currentLine?.options)
      : undefined;

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      !isChoiceLine ||
      !currentOptions ||
      optionIndex < 0 ||
      optionIndex >= currentOptions.length
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) => {
            if (line.id !== lineId || !isTaskEditorChoiceCellType(line.type)) {
              return line;
            }

            const baseOptions = getTaskEditorButtonGroupOptions(
              line.options
            ).map((option) => normalizeTaskEditorButtonGroupOption(option));
            const nextOptions = baseOptions.filter(
              (_option, index) => index !== optionIndex
            );

            return {
              ...line,
              options:
                nextOptions.length > 0
                  ? nextOptions
                  : [createTaskEditorButtonGroupOption()],
            };
          }),
        };
      })
    );
  };

  const handleQuizOptionCorrectToggle = (
    blockId: string,
    lineId: string,
    optionIndex: number,
    checked: boolean
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
    const currentOptions =
      currentLine?.type === "Quiz"
        ? getTaskEditorButtonGroupOptions(currentLine.options)
        : undefined;
    const currentValue =
      currentOptions && optionIndex >= 0 && optionIndex < currentOptions.length
        ? currentOptions[optionIndex].isCorrect === true
        : undefined;

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      currentLine?.type !== "Quiz" ||
      !currentOptions ||
      optionIndex < 0 ||
      optionIndex >= currentOptions.length ||
      currentValue === checked
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) => {
            if (line.id !== lineId || line.type !== "Quiz") {
              return line;
            }

            const nextOptions = getTaskEditorButtonGroupOptions(
              line.options
            ).map((option) => normalizeTaskEditorButtonGroupOption(option));
            nextOptions[optionIndex] = createTaskEditorButtonGroupOption({
              ...nextOptions[optionIndex],
              isCorrect: checked,
            });

            return {
              ...line,
              options: nextOptions,
            };
          }),
        };
      })
    );
  };

  const handleQuizIncorrectMessageChange = (
    blockId: string,
    lineId: string,
    value: string
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      currentLine?.type !== "Quiz" ||
      (currentLine.incorrectMessage ?? "") === value
    ) {
      return;
    }

    pushUndoSnapshotForTextEdit(`quiz-incorrect-message:${blockId}:${lineId}`);
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) =>
            line.id === lineId && line.type === "Quiz"
              ? {
                  ...line,
                  incorrectMessage: value,
                }
              : line
          ),
        };
      })
    );
  };

  const handleButtonSelectionAllowMultipleChange = (
    blockId: string,
    lineId: string,
    checked: boolean
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
    const currentValue =
      currentLine?.type === "ButtonSelection"
        ? currentLine.allowMultipleSelection !== false
        : undefined;

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      currentLine?.type !== "ButtonSelection" ||
      currentValue === checked
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) =>
            line.id === lineId && line.type === "ButtonSelection"
              ? {
                  ...line,
                  allowMultipleSelection: checked,
                }
              : line
          ),
        };
      })
    );
  };

  const handleTextareaPlaceholderChange = (
    blockId: string,
    lineId: string,
    value: string
  ) => {
    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      (currentLine?.type !== "Textarea" && currentLine?.type !== "Preset") ||
      (currentLine.textareaPlaceholder ?? "") === value
    ) {
      return;
    }

    pushUndoSnapshotForTextEdit(`textarea-placeholder:${blockId}:${lineId}`);
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) =>
            line.id === lineId
              ? {
                  ...line,
                  textareaPlaceholder: value,
                }
              : line
          ),
        };
      })
    );
  };

  const handleTypographyStyleChange = (
    blockId: string,
    lineId: string,
    style: TaskEditorTypographyStyle | null
  ) => {
    if (!style) {
      return;
    }

    const currentBlock = blocks.find((block) => block.id === blockId);
    const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

    if (
      !currentBlock ||
      !isTaskEditorNestedBlockType(currentBlock.type) ||
      currentLine?.type !== "Typography" ||
      (currentLine.typographyStyle ?? "Body") === style
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
          return block;
        }

        return {
          ...block,
          lines: (block.lines ?? []).map((line) =>
            line.id === lineId
              ? {
                  ...line,
                  typographyStyle: style,
                }
              : line
          ),
        };
      })
    );
  };

  const handleImageLineFileChange = async (
    blockId: string,
    lineId: string,
    file: File
  ) => {
    try {
      const imageDataUrl = await convertTaskEditorImageFileToDataUrl(file);
      const currentBlock = blocks.find((block) => block.id === blockId);
      const currentLine = currentBlock?.lines?.find(
        (line) => line.id === lineId
      );

      if (
        !currentBlock ||
        !isTaskEditorNestedBlockType(currentBlock.type) ||
        currentLine?.type !== "Image" ||
        currentLine.content === imageDataUrl
      ) {
        return;
      }

      resetTextEditUndoGrouping();
      pushUndoSnapshot();
      setBlocks((currentBlocks) =>
        currentBlocks.map((block) => {
          if (
            block.id !== blockId ||
            !isTaskEditorNestedBlockType(block.type)
          ) {
            return block;
          }

          return {
            ...block,
            lines: (block.lines ?? []).map((line) =>
              line.id === lineId
                ? {
                    ...line,
                    content: imageDataUrl,
                  }
                : line
            ),
          };
        })
      );
      setStatusMessage("이미지를 업로드했습니다.");
    } catch (error) {
      setStatusMessage("이미지 업로드에 실패했습니다.");
    }
  };

  const handleRemoveBlock = (blockId: string) => {
    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) => {
      const targetBlock = currentBlocks.find((block) => block.id === blockId);

      if (!targetBlock || isTaskEditorLastCardBlock(targetBlock)) {
        return currentBlocks;
      }

      const editableBlocks = currentBlocks.filter(
        (block) => !isTaskEditorLastCardBlock(block)
      );

      if (editableBlocks.length === 1) {
        return createEmptyTaskEditorState().blocks;
      }

      return currentBlocks.filter((block) => block.id !== blockId);
    });
  };

  const handleBlockDragStart = (
    event: React.DragEvent<HTMLElement>,
    blockId: string
  ) => {
    const targetBlock = blocks.find((block) => block.id === blockId);
    const blockCard = event.currentTarget.closest(
      "[data-testid='task-editor-block-card']"
    );

    if (!targetBlock || isTaskEditorLastCardBlock(targetBlock)) {
      event.preventDefault();
      return;
    }

    clearLineDragState();
    draggedBlockIdRef.current = blockId;
    didReorderBlockDuringDragRef.current = false;
    setDraggedBlockId(blockId);
    blockDropPreviewRef.current = null;
    setBlockDropPreview(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", blockId);

    if (
      blockCard instanceof HTMLElement &&
      typeof event.dataTransfer.setDragImage === "function"
    ) {
      const blockRect = blockCard.getBoundingClientRect();
      const fallbackOffsetX = Math.min(Math.max(blockRect.width / 2, 28), 120);
      const fallbackOffsetY = Math.min(Math.max(blockRect.height / 2, 28), 64);
      const offsetX =
        event.clientX > 0 ? event.clientX - blockRect.left : fallbackOffsetX;
      const offsetY =
        event.clientY > 0 ? event.clientY - blockRect.top : fallbackOffsetY;

      event.dataTransfer.setDragImage(
        blockCard,
        Math.max(24, Math.min(blockRect.width - 24, offsetX)),
        Math.max(24, Math.min(blockRect.height - 24, offsetY))
      );
    }
  };

  const handleBlockDragOver = (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorBlockDropPreview
  ) => {
    const sourceBlockId =
      draggedBlockIdRef.current ??
      draggedBlockId ??
      event.dataTransfer.getData("text/plain");

    if (!sourceBlockId) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    setBlockDropPreview((currentPreview) => {
      const resolvedPreview = isSameTaskEditorBlockDropPreview(
        currentPreview,
        nextPreview
      )
        ? currentPreview
        : nextPreview;

      blockDropPreviewRef.current = resolvedPreview;

      return resolvedPreview;
    });

    const sourceIndex = blocks.findIndex((block) => block.id === sourceBlockId);
    const previewIndex = getTaskEditorBlockReorderIndex(
      blocks,
      sourceBlockId,
      nextPreview
    );

    if (
      sourceIndex < 0 ||
      previewIndex === null ||
      previewIndex === sourceIndex ||
      isTaskEditorLastCardBlock(blocks[sourceIndex])
    ) {
      return;
    }

    if (!didReorderBlockDuringDragRef.current) {
      resetTextEditUndoGrouping();
      pushUndoSnapshot();
      didReorderBlockDuringDragRef.current = true;
    }

    setBlocks((currentBlocks) => {
      const currentSourceIndex = currentBlocks.findIndex(
        (block) => block.id === sourceBlockId
      );
      const currentTargetIndex = getTaskEditorBlockReorderIndex(
        currentBlocks,
        sourceBlockId,
        nextPreview
      );

      if (
        currentSourceIndex < 0 ||
        currentTargetIndex === null ||
        currentTargetIndex < 0 ||
        currentTargetIndex === currentSourceIndex ||
        isTaskEditorLastCardBlock(currentBlocks[currentSourceIndex])
      ) {
        return currentBlocks;
      }

      return reorderBlocks(
        currentBlocks,
        currentSourceIndex,
        currentTargetIndex
      );
    });
  };

  const getBlockCardDropPreview = (
    targetBlockId: string,
    event: React.DragEvent<HTMLElement>
  ) => {
    const activeDraggedBlockId = draggedBlockIdRef.current ?? draggedBlockId;

    if (!activeDraggedBlockId || targetBlockId === activeDraggedBlockId) {
      return null;
    }

    const targetBlockIndex = blocks.findIndex(
      (block) => block.id === targetBlockId
    );

    if (
      targetBlockIndex < 0 ||
      isTaskEditorLastCardBlock(blocks[targetBlockIndex])
    ) {
      return null;
    }

    const targetEditableIndex = blocks
      .slice(0, targetBlockIndex)
      .filter((block) => !isTaskEditorLastCardBlock(block)).length;
    const targetRect = event.currentTarget.getBoundingClientRect();
    const pointerY = event.clientY > 0 ? event.clientY : targetRect.bottom;
    const isBottomHalf = pointerY >= targetRect.top + targetRect.height / 2;

    return {
      insertionIndex: targetEditableIndex + (isBottomHalf ? 1 : 0),
    };
  };

  const handleBlockCardDragOver = (
    event: React.DragEvent<HTMLElement>,
    targetBlockId: string
  ) => {
    const nextPreview = getBlockCardDropPreview(targetBlockId, event);

    if (!nextPreview) {
      return;
    }

    handleBlockDragOver(event, nextPreview);
  };

  const handleBlockCardDrop = (
    event: React.DragEvent<HTMLElement>,
    targetBlockId: string
  ) => {
    const nextPreview = getBlockCardDropPreview(targetBlockId, event);

    if (!nextPreview) {
      return;
    }

    handleBlockDrop(event, nextPreview);
  };

  const handleBlockDrop = (
    event: React.DragEvent<HTMLElement>,
    preview: TaskEditorBlockDropPreview
  ) => {
    event.preventDefault();

    const sourceBlockId =
      draggedBlockIdRef.current ??
      draggedBlockId ??
      event.dataTransfer.getData("text/plain");
    const nextDropPreview = sourceBlockId
      ? (blockDropPreviewRef.current ?? blockDropPreview ?? preview)
      : null;

    clearBlockDragState();

    if (!sourceBlockId) {
      return;
    }

    const sourceIndex = blocks.findIndex((block) => block.id === sourceBlockId);
    const previewIndex = nextDropPreview
      ? getTaskEditorBlockReorderIndex(blocks, sourceBlockId, nextDropPreview)
      : null;

    if (sourceIndex < 0 || previewIndex === null) {
      return;
    }

    if (isTaskEditorLastCardBlock(blocks[sourceIndex])) {
      return;
    }

    if (previewIndex === sourceIndex) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) => {
      const currentSourceIndex = currentBlocks.findIndex(
        (block) => block.id === sourceBlockId
      );
      const currentTargetIndex = nextDropPreview
        ? getTaskEditorBlockReorderIndex(
            currentBlocks,
            sourceBlockId,
            nextDropPreview
          )
        : null;

      if (
        currentSourceIndex < 0 ||
        currentTargetIndex === null ||
        currentTargetIndex < 0
      ) {
        return currentBlocks;
      }

      if (isTaskEditorLastCardBlock(currentBlocks[currentSourceIndex])) {
        return currentBlocks;
      }

      return reorderBlocks(
        currentBlocks,
        currentSourceIndex,
        currentTargetIndex
      );
    });
  };

  const handleBlockDragEnd = () => {
    clearBlockDragState();
  };

  const handleLineDragStart = (
    event: React.DragEvent<HTMLElement>,
    blockId: string,
    lineId: string
  ) => {
    const targetBlock = blocks.find((block) => block.id === blockId);
    const targetLine = targetBlock?.lines?.find((line) => line.id === lineId);
    const lineCard = event.currentTarget.closest(
      "[data-testid='task-editor-line-card']"
    );

    if (
      !targetBlock ||
      !isTaskEditorNestedBlockType(targetBlock.type) ||
      !targetLine ||
      (targetBlock.lines?.length ?? 0) <= 1
    ) {
      event.preventDefault();
      return;
    }

    clearBlockDragState();

    const nextDraggedLine = {
      blockId,
      lineId,
    };

    draggedLineRef.current = nextDraggedLine;
    didReorderLineDuringDragRef.current = false;
    setDraggedLine(nextDraggedLine);
    lineDropPreviewRef.current = null;
    setLineDropPreview(null);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "application/task-editor-line",
      JSON.stringify(nextDraggedLine)
    );

    if (
      lineCard instanceof HTMLElement &&
      typeof event.dataTransfer.setDragImage === "function"
    ) {
      const lineRect = lineCard.getBoundingClientRect();
      const fallbackOffsetX = Math.min(Math.max(lineRect.width / 2, 28), 120);
      const fallbackOffsetY = Math.min(Math.max(lineRect.height / 2, 24), 64);
      const offsetX =
        event.clientX > 0 ? event.clientX - lineRect.left : fallbackOffsetX;
      const offsetY =
        event.clientY > 0 ? event.clientY - lineRect.top : fallbackOffsetY;

      event.dataTransfer.setDragImage(
        lineCard,
        Math.max(24, Math.min(lineRect.width - 24, offsetX)),
        Math.max(20, Math.min(lineRect.height - 20, offsetY))
      );
    }
  };

  const handleLineDragOver = (
    event: React.DragEvent<HTMLElement>,
    nextPreview: TaskEditorLineDropPreview
  ) => {
    const sourceLine =
      draggedLineRef.current ??
      draggedLine ??
      parseTaskEditorLineDragData(
        event.dataTransfer.getData("application/task-editor-line")
      );

    if (!sourceLine || sourceLine.blockId !== nextPreview.blockId) {
      return;
    }

    const sourceBlock = blocks.find((block) => block.id === sourceLine.blockId);

    if (!sourceBlock || !isTaskEditorNestedBlockType(sourceBlock.type)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    setLineDropPreview((currentPreview) => {
      const resolvedPreview = isSameTaskEditorLineDropPreview(
        currentPreview,
        nextPreview
      )
        ? currentPreview
        : nextPreview;

      lineDropPreviewRef.current = resolvedPreview;

      return resolvedPreview;
    });

    const sourceLines = sourceBlock.lines ?? [];
    const sourceIndex = sourceLines.findIndex(
      (line) => line.id === sourceLine.lineId
    );
    const previewIndex = getTaskEditorLineReorderIndex(
      sourceLines,
      sourceLine.lineId,
      nextPreview
    );

    if (
      sourceIndex < 0 ||
      previewIndex === null ||
      previewIndex === sourceIndex
    ) {
      return;
    }

    if (!didReorderLineDuringDragRef.current) {
      resetTextEditUndoGrouping();
      pushUndoSnapshot();
      didReorderLineDuringDragRef.current = true;
    }

    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (
          block.id !== sourceLine.blockId ||
          !isTaskEditorNestedBlockType(block.type)
        ) {
          return block;
        }

        const currentLines = block.lines ?? [];
        const currentSourceIndex = currentLines.findIndex(
          (line) => line.id === sourceLine.lineId
        );
        const currentTargetIndex = getTaskEditorLineReorderIndex(
          currentLines,
          sourceLine.lineId,
          nextPreview
        );

        if (
          currentSourceIndex < 0 ||
          currentTargetIndex === null ||
          currentTargetIndex < 0 ||
          currentTargetIndex === currentSourceIndex
        ) {
          return block;
        }

        return {
          ...block,
          lines: reorderLines(
            currentLines,
            currentSourceIndex,
            currentTargetIndex
          ),
        };
      })
    );
  };

  const getLineCardDropPreview = (
    blockId: string,
    targetLineId: string,
    event: React.DragEvent<HTMLElement>
  ) => {
    const activeDraggedLine = draggedLineRef.current ?? draggedLine;

    if (
      !activeDraggedLine ||
      activeDraggedLine.blockId !== blockId ||
      targetLineId === activeDraggedLine.lineId
    ) {
      return null;
    }

    const targetBlock = blocks.find((block) => block.id === blockId);

    if (!targetBlock || !isTaskEditorNestedBlockType(targetBlock.type)) {
      return null;
    }

    const targetLines = targetBlock.lines ?? [];
    const targetLineIndex = targetLines.findIndex(
      (line) => line.id === targetLineId
    );

    if (targetLineIndex < 0) {
      return null;
    }

    const targetRect = event.currentTarget.getBoundingClientRect();
    const pointerY = event.clientY > 0 ? event.clientY : targetRect.bottom;
    const isBottomHalf = pointerY >= targetRect.top + targetRect.height / 2;

    return {
      blockId,
      insertionIndex: targetLineIndex + (isBottomHalf ? 1 : 0),
    };
  };

  const handleLineCardDragOver = (
    event: React.DragEvent<HTMLElement>,
    blockId: string,
    targetLineId: string
  ) => {
    const nextPreview = getLineCardDropPreview(blockId, targetLineId, event);

    if (!nextPreview) {
      return;
    }

    handleLineDragOver(event, nextPreview);
  };

  const handleLineCardDrop = (
    event: React.DragEvent<HTMLElement>,
    blockId: string,
    targetLineId: string
  ) => {
    const nextPreview = getLineCardDropPreview(blockId, targetLineId, event);

    if (!nextPreview) {
      return;
    }

    handleLineDrop(event, nextPreview);
  };

  const handleLineDrop = (
    event: React.DragEvent<HTMLElement>,
    preview: TaskEditorLineDropPreview
  ) => {
    event.preventDefault();

    const sourceLine =
      draggedLineRef.current ??
      draggedLine ??
      parseTaskEditorLineDragData(
        event.dataTransfer.getData("application/task-editor-line")
      );
    const nextDropPreview =
      sourceLine && sourceLine.blockId === preview.blockId
        ? (lineDropPreviewRef.current ?? lineDropPreview ?? preview)
        : null;

    clearLineDragState();

    if (!sourceLine || !nextDropPreview) {
      return;
    }

    const sourceBlock = blocks.find((block) => block.id === sourceLine.blockId);

    if (!sourceBlock || !isTaskEditorNestedBlockType(sourceBlock.type)) {
      return;
    }

    const sourceLines = sourceBlock.lines ?? [];
    const sourceIndex = sourceLines.findIndex(
      (line) => line.id === sourceLine.lineId
    );
    const previewIndex = getTaskEditorLineReorderIndex(
      sourceLines,
      sourceLine.lineId,
      nextDropPreview
    );

    if (
      sourceIndex < 0 ||
      previewIndex === null ||
      previewIndex === sourceIndex
    ) {
      return;
    }

    resetTextEditUndoGrouping();
    pushUndoSnapshot();
    setBlocks((currentBlocks) =>
      currentBlocks.map((block) => {
        if (
          block.id !== sourceLine.blockId ||
          !isTaskEditorNestedBlockType(block.type)
        ) {
          return block;
        }

        const currentLines = block.lines ?? [];
        const currentSourceIndex = currentLines.findIndex(
          (line) => line.id === sourceLine.lineId
        );
        const currentTargetIndex = getTaskEditorLineReorderIndex(
          currentLines,
          sourceLine.lineId,
          nextDropPreview
        );

        if (
          currentSourceIndex < 0 ||
          currentTargetIndex === null ||
          currentTargetIndex < 0 ||
          currentTargetIndex === currentSourceIndex
        ) {
          return block;
        }

        return {
          ...block,
          lines: reorderLines(
            currentLines,
            currentSourceIndex,
            currentTargetIndex
          ),
        };
      })
    );
  };

  const handleLineDragEnd = () => {
    clearLineDragState();
  };

  const handleAIApply = () => {
    if (aiGenerateStatus.type !== "completed") {
      return;
    }

    if (selectedTaskId) {
      cacheTaskDraft(selectedTaskId);
    }

    resetTextEditUndoGrouping();
    setUndoHistory([]);
    setIsCreatingTask(true);
    setSelectedTaskId(undefined);
    setHighlightedTaskId(undefined);
    applyEditorState(aiGenerateStatus.result);
    dismissAIGenerate();
    setStatusMessage(
      "AI가 생성한 초안을 불러왔습니다. 내용을 확인하고 수정해주세요."
    );
  };

  const handleCreateTask = () => {
    if (selectedTaskId) {
      cacheTaskDraft(selectedTaskId);
    }

    resetTextEditUndoGrouping();
    setUndoHistory([]);
    setIsCreatingTask(true);
    setSelectedTaskId(undefined);
    setHighlightedTaskId(undefined);
    applyEditorState(createEmptyTaskEditorState());
    setStatusMessage(
      "새 활동지 초안을 만들었습니다. 변경 내용은 자동 저장됩니다."
    );
  };

  const handleLoadSavedTask = (nextTaskId: string) => {
    const nextTask = savedTasks.find((task) => task.taskId === nextTaskId);

    if (!nextTask) {
      setStatusMessage("저장된 활동지를 찾을 수 없습니다.");
      return;
    }

    if (selectedTaskId === nextTaskId || normalizedTaskId === nextTaskId) {
      setIsCreatingTask(true);
      setHighlightedTaskId(nextTaskId);
      setStatusMessage("편집 중인 초안을 유지합니다.");
      return;
    }

    if (selectedTaskId && selectedTaskId !== nextTaskId) {
      cacheTaskDraft(selectedTaskId);
    }

    const nextTaskDraft = taskDraftsByTaskId[nextTaskId];

    resetTextEditUndoGrouping();
    setIsCreatingTask(false);
    setUndoHistory([]);
    applyEditorState(nextTaskDraft ?? nextTask);
    setSelectedTaskId(nextTask.taskId);
    setHighlightedTaskId(nextTask.taskId);
    setStatusMessage(nextTaskDraft ? "편집 중인 변경사항을 불러왔습니다." : "");
  };

  const handleOpenDeleteTargetModal = (target: TaskEditorDeleteTarget) => {
    setPendingDeleteTarget(target);
  };

  const handleCloseDeleteTargetModal = () => {
    setPendingDeleteTarget(null);
  };

  const handleConfirmDeleteTarget = () => {
    if (!pendingDeleteTarget) {
      return;
    }

    if (pendingDeleteTarget.type === "block") {
      handleRemoveBlock(pendingDeleteTarget.blockId);
    } else {
      handleRemoveLine(pendingDeleteTarget.blockId, pendingDeleteTarget.lineId);
    }

    setPendingDeleteTarget(null);
  };

  const handleDeploy = async () => {
    if (isDeploying) {
      return undefined;
    }

    if (!normalizedTaskId) {
      setStatusMessage("배포하려면 활동지 ID를 먼저 입력해주세요.");
      return undefined;
    }

    if (isCurrentTaskIdTaken) {
      setStatusMessage(
        "이미 존재하는 활동지 ID입니다. 다른 활동지 ID를 사용하거나 기존 활동지를 선택해주세요."
      );
      return undefined;
    }

    if (deployChangeSummaries.length === 0) {
      setStatusMessage("배포할 변경 사항이 없습니다.");
      return selectedPublishedTask;
    }

    resetTextEditUndoGrouping();
    let savedTask: PublishedTaskEditorTask | undefined;
    setIsDeploying(true);

    try {
      savedTask = await publishTask(currentEditorState, selectedTaskId);
    } catch (error) {
      setStatusMessage(
        "배포 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
      return undefined;
    } finally {
      setIsDeploying(false);
    }

    if (!savedTask) {
      setStatusMessage("배포 결과를 확인하지 못했습니다. 다시 시도해주세요.");
      return undefined;
    }
    const nextSavedTask: PublishedTaskEditorTask = savedTask;

    if (selectedTaskId && selectedTaskId !== nextSavedTask.taskId) {
      removePersistedTaskDraft(selectedTaskId).catch(() => {});
    }
    persistTaskDraft(nextSavedTask.taskId, nextSavedTask).catch(() => {});

    setIsCreatingTask(false);
    setSelectedTaskId(nextSavedTask.taskId);
    setHighlightedTaskId(nextSavedTask.taskId);
    setUndoHistory([]);
    setStatusMessage(`변경 사항을 배포했습니다.`);

    return nextSavedTask;
  };

  const handleDeleteSavedTask = async (targetTaskId: string) => {
    if (isDeletingTask) {
      return;
    }

    setIsTaskMetadataMenuOpen(false);
    setIsDeletingTask(true);

    try {
      await removeTask(targetTaskId);
    } catch (error) {
      setStatusMessage(
        "활동지 삭제 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
      return;
    } finally {
      setIsDeletingTask(false);
    }

    setIsDeleteModalOpen(false);
    removeTaskDraft(targetTaskId);

    if (highlightedTaskId === targetTaskId) {
      setHighlightedTaskId(undefined);
    }

    if (selectedTaskId === targetTaskId || normalizedTaskId === targetTaskId) {
      setIsCreatingTask(false);
      setUndoHistory([]);
      setSelectedTaskId(undefined);
      applyEditorState(createEmptyTaskEditorState());
    }

    setStatusMessage(`${targetTaskId} 활동지를 삭제했습니다.`);
  };

  const isTaskMetadataDeleteDisabled =
    !deletableTaskId || isStoreLoading || isDeploying || isDeletingTask;
  const taskMetadataHeaderAction = isTaskEditorActive ? (
    <Box sx={{ position: "relative" }}>
      <IconButton
        size="sm"
        variant="plain"
        color="neutral"
        aria-label="활동지 메타데이터 메뉴 열기"
        data-testid="task-editor-task-metadata-menu-button"
        disabled={isTaskMetadataDeleteDisabled}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          setIsTaskMetadataMenuOpen((currentOpen) => !currentOpen);
        }}
        sx={{
          width: "36px",
          height: "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: "24px",
          }}
        >
          more_vert
        </span>
      </IconButton>
      {isTaskMetadataMenuOpen && (
        <Box
          className="absolute z-[30] right-0 top-[110%]"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <List
            orientation="vertical"
            sx={{
              width: "max-content",
              borderRadius: "12px",
              boxShadow:
                "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
              overflow: "hidden",
              borderWidth: 1,
              borderColor: theme.vars.palette.Border.SuperWeak,
              backgroundColor: theme.vars.palette.Surface.Forward,
              p: 0,
              "--List-gap": 0,
              "--ListItem-paddingY": 0,
            }}
          >
            <ListItem sx={{ p: 0 }}>
              <Button
                size="sm"
                variant="Plain"
                color="neutral"
                disabled={isTaskMetadataDeleteDisabled}
                startDecorator={<TrashIconSvg />}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();

                  if (!deletableTaskId) {
                    return;
                  }

                  setIsTaskMetadataMenuOpen(false);
                  setIsDeleteModalOpen(true);
                }}
                sx={{
                  "--Button-gap": "12px",
                  width: "100%",
                  height: "44px",
                  fontSize: "16px",
                  fontWeight: 500,
                  pl: "20px",
                  pr: "24px",
                  justifyContent: "flex-start",
                  color: theme.vars.palette.Content.Strong,
                  backgroundColor: theme.vars.palette.Surface.Forward,
                }}
              >
                {"삭제하기"}
              </Button>
            </ListItem>
          </List>
        </Box>
      )}
    </Box>
  ) : undefined;

  const taskListCreateButton = (
    <Button
      size="sm"
      variant="Outlined"
      onClick={handleCreateTask}
      disabled={isTaskListLoading}
      aria-label="새 활동지 생성"
      sx={{
        minWidth: "36px",
        width: "36px",
        height: "32px",
        px: 0,
        fontWeight: 700,
      }}
    >
      <AddIcon size={16} />
    </Button>
  );

  return (
    <FullPageContainer
      backgroundColor={sidebarBackgroundColor}
      hasFooter={false}
      disableBottomSafeArea
    >
      <Stack
        direction="row"
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Sheet
          sx={{
            width: isTaskListCollapsed
              ? taskListCollapsedPanelWidth
              : taskListPanelWidth,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid",
            borderColor: "divider",
            backgroundColor: sidebarBackgroundColor,
            overflow: "hidden",
            flexShrink: 0,
            transition: "width 0.2s ease",
          }}
        >
          {isTaskListCollapsed ? (
            <Box
              sx={{
                width: "100%",
                minHeight: panelHeaderHeight,
                height: panelHeaderHeight,
                backgroundColor: sidebarBackgroundColor,
                px: "8px",
                flexShrink: 0,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                {taskListCreateButton}
              </Stack>
            </Box>
          ) : (
            renderPanelHeader(
              "활동지 목록",
              sidebarBackgroundColor,
              taskListCreateButton
            )
          )}

          {!isTaskListCollapsed ? (
            <Box
              sx={{
                ...panelScrollbarSx,
                flexGrow: 1,
                px: "8px",
                py: "8px",
              }}
              className="scrollbar-hide"
            >
              {isTaskListLoading ? (
                <Stack
                  sx={{
                    width: "100%",
                    minHeight: taskListLoadingContainerHeight,
                  }}
                  alignItems="center"
                  justifyContent="center"
                >
                  <CircularProgress color="neutral" />
                </Stack>
              ) : savedTasks.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="Caption"
                    sx={{ color: theme.vars.palette.Content.Weak }}
                  >
                    아직 저장된 활동지가 없습니다.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing="12px">
                  {taskListSections.map((section) => (
                    <Stack key={section.key} spacing="6px">
                      <Box sx={{ px: "8px" }}>
                        <Typography
                          variant="Caption"
                          sx={{
                            color: theme.vars.palette.Content.Weak,
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                          }}
                        >
                          {section.label}
                        </Typography>
                      </Box>

                      <List
                        sx={{
                          "--List-gap": "4px",
                          "--ListItem-paddingX": 0,
                          "--ListItem-paddingY": 0,
                          px: 0,
                          py: 0,
                        }}
                      >
                        {section.tasks.map((savedTask) => {
                          const isSelected =
                            savedTask.taskId === highlightedTaskId;

                          return (
                            <ListItem key={savedTask.taskId} sx={{ p: 0 }}>
                              <ListItemButton
                                selected={isSelected}
                                color={isSelected ? "primary" : "neutral"}
                                onClick={() =>
                                  handleLoadSavedTask(savedTask.taskId)
                                }
                                sx={{
                                  borderRadius: "8px",
                                  minHeight: "60px",
                                  px: "12px",
                                  py: "8px",
                                  alignItems: "center",
                                }}
                              >
                                <Stack spacing="2px" sx={{ width: "100%" }}>
                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ minWidth: 0 }}
                                  >
                                    <Typography
                                      variant="Caption"
                                      sx={{
                                        color:
                                          theme.vars.palette.Content.Strong,
                                        fontWeight: 500,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {savedTask.title || "새 활동지 제목"}
                                    </Typography>
                                    {savedTask.isKeyActivity && (
                                      <Chip
                                        size="sm"
                                        variant="outlined"
                                        color="primary"
                                        sx={{
                                          flexShrink: 0,
                                          minHeight: "20px",
                                          fontSize: "11px",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Key
                                      </Chip>
                                    )}
                                  </Stack>
                                  <Typography
                                    variant="Caption"
                                    sx={{
                                      color: theme.vars.palette.Content.Weak,
                                    }}
                                  >
                                    {savedTask.taskId}
                                  </Typography>
                                </Stack>
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, minHeight: 0 }} />
          )}
          <Box
            sx={{
              px: "8px",
              py: "8px",
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconButton
              size="sm"
              aria-label={
                isTaskListCollapsed ? "활동지 목록 펼치기" : "활동지 목록 접기"
              }
              data-testid="task-editor-task-list-collapse-toggle"
              onClick={() =>
                setIsTaskListCollapsed((currentCollapsed) => !currentCollapsed)
              }
              sx={{
                width: "34px",
                height: "34px",
                borderRadius: "999px",
                color: "#111111",
              }}
            >
              <span
                style={{ fontSize: "18px" }}
                className="material-symbols-rounded"
              >
                {isTaskListCollapsed
                  ? "keyboard_double_arrow_right"
                  : "keyboard_double_arrow_left"}
              </span>
            </IconButton>
          </Box>
        </Sheet>

        <Sheet
          sx={{
            width: taskPropertyPanelWidth,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid",
            borderColor: "divider",
            backgroundColor: sidebarBackgroundColor,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {renderPanelHeader(
            "활동지 메타데이터",
            sidebarBackgroundColor,
            taskMetadataHeaderAction
          )}

          <Box
            sx={{
              ...panelScrollbarSx,
              flexGrow: 1,
              px: "20px",
              py: "16px",
            }}
            className="scrollbar-hide"
          >
            {isTaskMetadataLoading ? (
              <Box sx={{ py: "4px" }}>
                <Typography
                  variant="Caption"
                  sx={{ color: theme.vars.palette.Content.Weak }}
                >
                  활동지 메타데이터를 불러오는 중입니다...
                </Typography>
              </Box>
            ) : isTaskEditorActive ? (
              <Stack spacing={1.5}>
                <Stack spacing={0.75}>
                  {renderMetaFieldLabel("활동지 ID", true)}
                  <Input
                    placeholder="활동지 ID를 입력하세요. 예: 1-1-A"
                    value={taskId}
                    onChange={(event) => handleTaskIdChange(event.target.value)}
                    onBlur={resetTextEditUndoGrouping}
                    sx={{ width: "100%" }}
                  />
                </Stack>
                {normalizedTaskId && isCurrentTaskIdTaken && (
                  <Typography variant="Caption" sx={{ color: "danger.500" }}>
                    이미 저장된 활동지 ID입니다. 다른 활동지 ID를 사용하거나
                    기존 활동지를 선택해주세요.
                  </Typography>
                )}
                <Stack spacing={0.75}>
                  {renderMetaFieldLabel("활동지 제목", true)}
                  <Input
                    placeholder="활동지 제목을 입력하세요. 예: 나의 고민과 목표"
                    value={title}
                    onChange={(event) => handleTitleChange(event.target.value)}
                    onBlur={resetTextEditUndoGrouping}
                    sx={{ width: "100%" }}
                  />
                </Stack>
                <Stack spacing={0.75}>
                  {renderMetaFieldLabel("세트 번호", true)}
                  <Input
                    type="number"
                    placeholder="세트 번호를 입력하세요. 예: 1"
                    value={setNumber ?? ""}
                    onChange={(event) =>
                      handleSetNumberChange(event.target.value)
                    }
                    onBlur={resetTextEditUndoGrouping}
                    sx={{ width: "100%" }}
                  />
                </Stack>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {renderMetaFieldLabel("Key 활동지 여부")}
                    <Checkbox
                      checked={isKeyActivity}
                      disabled={isKeyActivityCheckboxDisabled}
                      onChange={(event) =>
                        handleKeyActivityChange(event.target.checked)
                      }
                      slotProps={{
                        input: {
                          "aria-label": "Key 활동지 여부",
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            ) : (
              <Box sx={{ py: "4px" }}>
                <Typography
                  variant="Caption"
                  sx={{ color: theme.vars.palette.Content.Weak }}
                >
                  왼쪽 목록에서 활동지를 선택해주세요.
                </Typography>
              </Box>
            )}
          </Box>

          {isTaskEditorActive && (
            <Box
              sx={{
                px: "10px",
                pt: "10px",
                pb: "12px",
                flexShrink: 0,
              }}
            >
              <Stack spacing={1.5}>
                {statusMessage && (
                  <Typography variant="Caption" sx={{ color: "primary.500" }}>
                    {statusMessage}
                  </Typography>
                )}
                {selectedPublishedTask && (
                  <Sheet
                    variant="soft"
                    sx={{
                      p: "12px",
                      borderRadius: "12px",
                      backgroundColor: theme.vars.palette.Background.Level1,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography
                        variant="Caption"
                        sx={{
                          color: theme.vars.palette.Content.Strong,
                          fontWeight: 700,
                        }}
                      >
                        최근 배포 스냅샷
                      </Typography>
                      <Typography
                        variant="Caption"
                        sx={{ color: theme.vars.palette.Content.Weak }}
                      >
                        Version ID: {selectedPublishedTask.versionId}
                      </Typography>
                      <Typography
                        variant="Caption"
                        sx={{ color: theme.vars.palette.Content.Weak }}
                      >
                        배포 시각:{" "}
                        {formatVersionTimestamp(
                          selectedPublishedTask.updatedAt
                        )}
                      </Typography>
                      {/*<Typography*/}
                      {/*  variant="Caption"*/}
                      {/*  sx={{ color: theme.vars.palette.Content.Weak }}*/}
                      {/*>*/}
                      {/*  배포자:{" "}*/}
                      {/*  {getTaskEditorDeployerDisplayName(*/}
                      {/*    selectedPublishedTask.updatedByUserId*/}
                      {/*  )}*/}
                      {/*</Typography>*/}
                    </Stack>
                  </Sheet>
                )}
                <Button
                  variant="Soft"
                  color="neutral"
                  onClick={() => setIsAIGenerateModalOpen(true)}
                  disabled={aiGenerateStatus.type === "generating"}
                  sx={{ height: "42px" }}
                >
                  {"AI로 초안 생성"}
                </Button>
                <TaskEditorAIStatusBanner
                  status={aiGenerateStatus}
                  onApply={handleAIApply}
                  onPreview={() => setIsAIPreviewModalOpen(true)}
                  onDismiss={dismissAIGenerate}
                />
                <Button
                  onClick={handleDeploy}
                  loading={isDeploying}
                  disabled={
                    !canDeploy ||
                    isStoreLoading ||
                    isDeploying ||
                    isDeletingTask
                  }
                >
                  {"변경 사항 배포하기"}
                </Button>
              </Stack>
            </Box>
          )}
        </Sheet>

        <Sheet
          sx={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.level1",
            overflow: "hidden",
          }}
        >
          {renderPanelHeader(
            "활동지 내용",
            theme.vars.palette.Background.Level1
          )}

          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {isTaskDraftLoading ? (
              <Box sx={{ px: "20px", py: "16px" }}>
                <Typography
                  variant="Caption"
                  sx={{ color: theme.vars.palette.Content.Weak }}
                >
                  초안을 불러오는 중입니다...
                </Typography>
              </Box>
            ) : isTaskEditorActive ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    ...panelScrollbarSx,
                    px: "20px",
                    py: "16px",
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                  className="scrollbar-hide"
                  data-testid="task-editor-block-panel"
                >
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <Typography
                        variant="Body"
                        sx={{
                          color: theme.vars.palette.Content.Strong,
                          fontWeight: 700,
                        }}
                      >
                        자주 쓰는 블록
                      </Typography>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        {TASK_EDITOR_EDITABLE_BLOCK_TYPES.map((type) => (
                          <Button
                            key={type}
                            variant="Soft"
                            onClick={() => handleAddBlock(type)}
                          >
                            {getTaskEditorBlockTypeLabel(type)} 추가
                          </Button>
                        ))}
                      </Stack>
                    </Stack>

                    <Stack spacing={0}>
                      {renderedBlocks.some(
                        (block) => !isTaskEditorLastCardBlock(block)
                      ) &&
                        (() => {
                          const firstEditableBlock = renderedBlocks.find(
                            (block) => !isTaskEditorLastCardBlock(block)
                          );
                          const isPreviewActive =
                            isSameTaskEditorBlockDropPreview(blockDropPreview, {
                              insertionIndex: 0,
                            });

                          return firstEditableBlock ? (
                            <Box
                              key={`drop-zone-before-${firstEditableBlock.id}`}
                              data-testid="task-editor-block-drop-zone"
                              data-drop-preview={
                                isPreviewActive ? "active" : "inactive"
                              }
                              onDragOver={(event) =>
                                handleBlockDragOver(event, {
                                  insertionIndex: 0,
                                })
                              }
                              onDrop={(event) =>
                                handleBlockDrop(event, { insertionIndex: 0 })
                              }
                              sx={{
                                ...taskEditorBlockDropZoneSx,
                                height: isPreviewActive
                                  ? "28px"
                                  : taskEditorBlockDropZoneSx.height,
                                opacity: draggedBlockId ? 1 : 0,
                                transform: isPreviewActive
                                  ? "scaleY(1)"
                                  : "scaleY(0.92)",
                              }}
                            />
                          ) : null;
                        })()}

                      {renderedBlocks.map((block, index) =>
                        (() => {
                          const isDraggedBlock =
                            draggedBlockId === block.id &&
                            !isTaskEditorLastCardBlock(block);
                          const isEditableBlock =
                            !isTaskEditorLastCardBlock(block);
                          const showAfterDropZone = isEditableBlock;
                          const editableBlockIndex =
                            renderedBlocks
                              .slice(0, index + 1)
                              .filter(
                                (nextBlock) =>
                                  !isTaskEditorLastCardBlock(nextBlock)
                              ).length - 1;
                          const isAfterPreviewActive =
                            showAfterDropZone &&
                            isSameTaskEditorBlockDropPreview(blockDropPreview, {
                              insertionIndex: editableBlockIndex + 1,
                            });

                          return (
                            <React.Fragment key={block.id}>
                              <Sheet
                                sx={{
                                  ...taskEditorBlockCardSx,
                                  opacity: isDraggedBlock ? 0.96 : 1,
                                  transform: isDraggedBlock
                                    ? "translateY(-10px) scale(1.015)"
                                    : "none",
                                  boxShadow: isDraggedBlock
                                    ? "0 28px 64px rgba(15, 23, 42, 0.16)"
                                    : taskEditorBlockCardSx.boxShadow,
                                  borderColor: isDraggedBlock
                                    ? theme.vars.palette.primary.soft
                                    : taskEditorBlockCardSx.borderColor,
                                  zIndex: isDraggedBlock ? 2 : 1,
                                  mb: 0,
                                }}
                                data-testid="task-editor-block-card"
                                data-block-id={block.id}
                                data-dragging={
                                  isDraggedBlock ? "true" : "false"
                                }
                                data-drop-preview="none"
                                onDragOver={(event) =>
                                  handleBlockCardDragOver(event, block.id)
                                }
                                onDrop={(event) =>
                                  handleBlockCardDrop(event, block.id)
                                }
                              >
                                <Stack
                                  spacing={1.75}
                                  sx={{ position: "relative" }}
                                >
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "stretch", sm: "center" }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                    >
                                      <IconButton
                                        size="sm"
                                        aria-label={`블록 ${index + 1} 이동`}
                                        data-testid={`task-editor-drag-block-${block.id}`}
                                        draggable={
                                          !isTaskEditorLastCardBlock(block)
                                        }
                                        disabled={isTaskEditorLastCardBlock(
                                          block
                                        )}
                                        onDragStart={(event) =>
                                          handleBlockDragStart(event, block.id)
                                        }
                                        onDragEnd={handleBlockDragEnd}
                                        sx={{
                                          cursor: isTaskEditorLastCardBlock(
                                            block
                                          )
                                            ? "not-allowed"
                                            : isDraggedBlock
                                              ? "grabbing"
                                              : "grab",
                                        }}
                                      >
                                        <span
                                          style={{ fontSize: "18px" }}
                                          className="material-symbols-rounded"
                                        >
                                          drag_indicator
                                        </span>
                                      </IconButton>
                                      <Typography
                                        variant="Body"
                                        sx={{
                                          color:
                                            theme.vars.palette.Content.Strong,
                                          fontWeight: 700,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.75,
                                        }}
                                      >
                                        {isTaskEditorLastCardBlock(block)
                                          ? "마치기 블록 묶음"
                                          : `블록 ${index + 1}`}
                                      </Typography>
                                      {!isTaskEditorLastCardBlock(block) && (
                                        <Select<TaskEditorBlockType>
                                          size="sm"
                                          value={block.type}
                                          onChange={(_, value) => {
                                            if (!value) {
                                              return;
                                            }

                                            handleBlockTypeChange(
                                              block.id,
                                              value
                                            );
                                          }}
                                          sx={{
                                            ...taskEditorSelectSx,
                                            minWidth: { xs: "100%", sm: 132 },
                                          }}
                                          slotProps={{
                                            button: {
                                              "data-testid": `task-editor-block-type-select-${block.id}`,
                                              sx: taskEditorSelectButtonSx,
                                            },
                                            listbox: {
                                              sx: taskEditorSelectListboxSx,
                                            },
                                          }}
                                        >
                                          {TASK_EDITOR_EDITABLE_BLOCK_TYPES.map(
                                            (type) => (
                                              <Option key={type} value={type}>
                                                {getTaskEditorBlockTypeLabel(
                                                  type
                                                )}
                                              </Option>
                                            )
                                          )}
                                        </Select>
                                      )}
                                    </Stack>
                                    <Stack
                                      direction={{ xs: "column", sm: "row" }}
                                      spacing={1}
                                    >
                                      {isTaskEditorNestedBlockType(
                                        block.type
                                      ) && !isTaskEditorLastCardBlock(block) ? (
                                        <Button
                                          size="sm"
                                          variant="Plain"
                                          onClick={() =>
                                            handleAddLine(block.id)
                                          }
                                        >
                                          컴포넌트 추가
                                        </Button>
                                      ) : null}
                                      {!isTaskEditorLastCardBlock(block) && (
                                        <IconButton
                                          size="sm"
                                          aria-label="블록 삭제"
                                          data-testid={`task-editor-delete-block-${block.id}`}
                                          onClick={() =>
                                            handleOpenDeleteTargetModal({
                                              type: "block",
                                              blockId: block.id,
                                            })
                                          }
                                        >
                                          <span
                                            style={{ fontSize: "16px" }}
                                            className="material-symbols-rounded"
                                          >
                                            close
                                          </span>
                                        </IconButton>
                                      )}
                                    </Stack>
                                  </Stack>

                                  {isTaskEditorNestedBlockType(block.type) &&
                                  !isTaskEditorLastCardBlock(block) ? (
                                    <Stack spacing={1.5}>
                                      <Stack spacing={0}>
                                        {(() => {
                                          const blockLines = block.lines ?? [];
                                          const isLineDragActive =
                                            draggedLine?.blockId === block.id;
                                          const isBeforePreviewActive =
                                            isSameTaskEditorLineDropPreview(
                                              lineDropPreview,
                                              {
                                                blockId: block.id,
                                                insertionIndex: 0,
                                              }
                                            );

                                          return (
                                            <>
                                              {blockLines.length > 0 ? (
                                                <Box
                                                  data-testid="task-editor-line-drop-zone"
                                                  data-drop-preview={
                                                    isBeforePreviewActive
                                                      ? "active"
                                                      : "inactive"
                                                  }
                                                  onDragOver={(event) =>
                                                    handleLineDragOver(event, {
                                                      blockId: block.id,
                                                      insertionIndex: 0,
                                                    })
                                                  }
                                                  onDrop={(event) =>
                                                    handleLineDrop(event, {
                                                      blockId: block.id,
                                                      insertionIndex: 0,
                                                    })
                                                  }
                                                  sx={{
                                                    ...taskEditorLineDropZoneSx,
                                                    height:
                                                      isBeforePreviewActive
                                                        ? "24px"
                                                        : taskEditorLineDropZoneSx.height,
                                                    opacity: isLineDragActive
                                                      ? 1
                                                      : 0,
                                                    transform:
                                                      isBeforePreviewActive
                                                        ? "scaleY(1)"
                                                        : "scaleY(0.92)",
                                                  }}
                                                />
                                              ) : null}
                                              {blockLines.map(
                                                (line, lineIndex) => {
                                                  const isDraggedLine =
                                                    draggedLine?.blockId ===
                                                      block.id &&
                                                    draggedLine.lineId ===
                                                      line.id;
                                                  const canDragLine =
                                                    blockLines.length > 1;
                                                  const isAfterPreviewActive =
                                                    isSameTaskEditorLineDropPreview(
                                                      lineDropPreview,
                                                      {
                                                        blockId: block.id,
                                                        insertionIndex:
                                                          lineIndex + 1,
                                                      }
                                                    );

                                                  return (
                                                    <React.Fragment
                                                      key={line.id}
                                                    >
                                                      <Sheet
                                                        sx={{
                                                          ...taskEditorLineCardSx,
                                                          opacity: isDraggedLine
                                                            ? 0.96
                                                            : 1,
                                                          transform:
                                                            isDraggedLine
                                                              ? "translateY(-6px) scale(1.01)"
                                                              : "none",
                                                          boxShadow:
                                                            isDraggedLine
                                                              ? "0 18px 40px rgba(15, 23, 42, 0.12)"
                                                              : taskEditorLineCardSx.boxShadow,
                                                          borderColor:
                                                            isDraggedLine
                                                              ? theme.vars
                                                                  .palette
                                                                  .primary.soft
                                                              : taskEditorLineCardSx.borderColor,
                                                          zIndex: isDraggedLine
                                                            ? 2
                                                            : 1,
                                                        }}
                                                        data-testid="task-editor-line-card"
                                                        data-line-id={line.id}
                                                        data-dragging={
                                                          isDraggedLine
                                                            ? "true"
                                                            : "false"
                                                        }
                                                        onDragOver={(event) =>
                                                          handleLineCardDragOver(
                                                            event,
                                                            block.id,
                                                            line.id
                                                          )
                                                        }
                                                        onDrop={(event) =>
                                                          handleLineCardDrop(
                                                            event,
                                                            block.id,
                                                            line.id
                                                          )
                                                        }
                                                      >
                                                        <Stack spacing={1.25}>
                                                          <Stack
                                                            direction={{
                                                              xs: "column",
                                                              sm: "row",
                                                            }}
                                                            spacing={1}
                                                            justifyContent="space-between"
                                                            alignItems={{
                                                              xs: "stretch",
                                                              sm: "center",
                                                            }}
                                                          >
                                                            <Stack
                                                              direction={{
                                                                xs: "column",
                                                                sm: "row",
                                                              }}
                                                              spacing={1}
                                                              alignItems={{
                                                                xs: "stretch",
                                                                sm: "center",
                                                              }}
                                                              sx={{
                                                                minWidth: 0,
                                                              }}
                                                            >
                                                              <IconButton
                                                                size="sm"
                                                                aria-label="컴포넌트 이동"
                                                                data-testid={`task-editor-drag-line-${line.id}`}
                                                                draggable={
                                                                  canDragLine
                                                                }
                                                                disabled={
                                                                  !canDragLine
                                                                }
                                                                onDragStart={(
                                                                  event
                                                                ) =>
                                                                  handleLineDragStart(
                                                                    event,
                                                                    block.id,
                                                                    line.id
                                                                  )
                                                                }
                                                                onDragEnd={
                                                                  handleLineDragEnd
                                                                }
                                                                sx={{
                                                                  cursor:
                                                                    !canDragLine
                                                                      ? "not-allowed"
                                                                      : isDraggedLine
                                                                        ? "grabbing"
                                                                        : "grab",
                                                                }}
                                                              >
                                                                <span
                                                                  style={{
                                                                    fontSize:
                                                                      "18px",
                                                                  }}
                                                                  className="material-symbols-rounded"
                                                                >
                                                                  drag_indicator
                                                                </span>
                                                              </IconButton>
                                                              <Typography
                                                                variant="Caption"
                                                                sx={
                                                                  taskEditorLineIndexLabelSx
                                                                }
                                                              >
                                                                {`컴포넌트 ${lineIndex + 1}`}
                                                              </Typography>
                                                              <Select<TaskEditorCellType>
                                                                size="sm"
                                                                value={
                                                                  line.type
                                                                }
                                                                onChange={(
                                                                  _,
                                                                  value
                                                                ) =>
                                                                  handleCellTypeChange(
                                                                    block.id,
                                                                    line.id,
                                                                    line.id,
                                                                    value
                                                                  )
                                                                }
                                                                sx={{
                                                                  ...taskEditorSelectSx,
                                                                  minWidth: {
                                                                    xs: "100%",
                                                                    sm: 140,
                                                                  },
                                                                }}
                                                                slotProps={{
                                                                  button: {
                                                                    "data-testid": `task-editor-element-type-select-${line.id}`,
                                                                    sx: taskEditorSelectButtonSx,
                                                                  },
                                                                  listbox: {
                                                                    sx: taskEditorSelectListboxSx,
                                                                  },
                                                                }}
                                                              >
                                                                {TASK_EDITOR_EDITABLE_CELL_TYPES.map(
                                                                  (
                                                                    cellType
                                                                  ) => (
                                                                    <Option
                                                                      key={`${line.id}-${cellType}`}
                                                                      value={
                                                                        cellType
                                                                      }
                                                                    >
                                                                      {getTaskEditorCellTypeLabel(
                                                                        cellType
                                                                      )}
                                                                    </Option>
                                                                  )
                                                                )}
                                                              </Select>
                                                            </Stack>
                                                            <IconButton
                                                              size="sm"
                                                              aria-label="컴포넌트 삭제"
                                                              data-testid={`task-editor-delete-line-${line.id}`}
                                                              onClick={() =>
                                                                handleOpenDeleteTargetModal(
                                                                  {
                                                                    type: "line",
                                                                    blockId:
                                                                      block.id,
                                                                    lineId:
                                                                      line.id,
                                                                  }
                                                                )
                                                              }
                                                            >
                                                              <span
                                                                style={{
                                                                  fontSize:
                                                                    "16px",
                                                                }}
                                                                className="material-symbols-rounded"
                                                              >
                                                                close
                                                              </span>
                                                            </IconButton>
                                                          </Stack>
                                                          <Stack
                                                            spacing={0.75}
                                                            sx={{ minWidth: 0 }}
                                                            data-testid="task-editor-element-card"
                                                          >
                                                            {isTaskEditorChoiceCellType(
                                                              line.type
                                                            ) ? (
                                                              <Box
                                                                sx={
                                                                  taskEditorButtonGroupEditorSx
                                                                }
                                                                data-testid={
                                                                  line.type ===
                                                                  "Quiz"
                                                                    ? "task-editor-quiz-editor"
                                                                    : line.type ===
                                                                        "ButtonSelection"
                                                                      ? "task-editor-button-selection-editor"
                                                                      : "task-editor-button-group-editor"
                                                                }
                                                              >
                                                                <Stack
                                                                  spacing={0.5}
                                                                  sx={
                                                                    taskEditorCompactFieldSx
                                                                  }
                                                                >
                                                                  <Typography
                                                                    variant="Caption"
                                                                    sx={
                                                                      taskEditorCompactFieldLabelSx
                                                                    }
                                                                  >
                                                                    ID
                                                                  </Typography>
                                                                  <Input
                                                                    size="sm"
                                                                    sx={
                                                                      taskEditorCompactFieldSx
                                                                    }
                                                                    placeholder="예: abc1"
                                                                    value={
                                                                      line.content
                                                                    }
                                                                    onChange={(
                                                                      event
                                                                    ) =>
                                                                      handleContentLineCellChange(
                                                                        block.id,
                                                                        line.id,
                                                                        line.id,
                                                                        event
                                                                          .target
                                                                          .value
                                                                      )
                                                                    }
                                                                    onBlur={
                                                                      resetTextEditUndoGrouping
                                                                    }
                                                                  />
                                                                </Stack>
                                                                <Stack
                                                                  spacing={0.5}
                                                                  sx={
                                                                    taskEditorCompactFieldSx
                                                                  }
                                                                >
                                                                  <Box
                                                                    sx={
                                                                      taskEditorButtonGroupGuideTriggerSx
                                                                    }
                                                                  >
                                                                    <Typography
                                                                      variant="Caption"
                                                                      sx={
                                                                        taskEditorCompactFieldLabelSx
                                                                      }
                                                                    >
                                                                      옵션
                                                                    </Typography>
                                                                    {line.type ===
                                                                    "ButtonGroup" ? (
                                                                      <Tooltip
                                                                        arrow
                                                                        placement="top-start"
                                                                        sx={
                                                                          taskEditorButtonGroupGuideTooltipSlotSx
                                                                        }
                                                                        slotProps={{
                                                                          arrow:
                                                                            {
                                                                              sx: taskEditorButtonGroupGuideTooltipArrowSx,
                                                                            },
                                                                        }}
                                                                        title={
                                                                          <Typography
                                                                            component="div"
                                                                            sx={
                                                                              taskEditorButtonGroupGuideTooltipSx
                                                                            }
                                                                          >
                                                                            {
                                                                              TASK_EDITOR_BUTTON_GROUP_GUIDE_TEXT
                                                                            }
                                                                          </Typography>
                                                                        }
                                                                      >
                                                                        <IconButton
                                                                          size="sm"
                                                                          variant="plain"
                                                                          color="neutral"
                                                                          aria-label="옵션 분기 가이드"
                                                                          sx={
                                                                            taskEditorButtonGroupGuideIconSx
                                                                          }
                                                                        >
                                                                          <span
                                                                            style={{
                                                                              fontSize:
                                                                                "14px",
                                                                            }}
                                                                            className="material-symbols-rounded"
                                                                          >
                                                                            help
                                                                          </span>
                                                                        </IconButton>
                                                                      </Tooltip>
                                                                    ) : null}
                                                                  </Box>
                                                                  <Box
                                                                    sx={
                                                                      taskEditorButtonGroupOptionListSx
                                                                    }
                                                                  >
                                                                    {getTaskEditorButtonGroupOptions(
                                                                      line.options
                                                                    ).map(
                                                                      (
                                                                        option,
                                                                        optionIndex
                                                                      ) => {
                                                                        const branchTargetBlocks =
                                                                          getTaskEditorButtonGroupBranchTargetBlocks(
                                                                            renderedBlocks,
                                                                            block.id
                                                                          );
                                                                        const getBranchBlockLabel =
                                                                          (
                                                                            targetBlockId?: string
                                                                          ) => {
                                                                            const targetIndex =
                                                                              renderedBlocks.findIndex(
                                                                                (
                                                                                  candidateBlock
                                                                                ) =>
                                                                                  candidateBlock.id ===
                                                                                  targetBlockId
                                                                              );
                                                                            const targetBlock =
                                                                              targetIndex >
                                                                              -1
                                                                                ? renderedBlocks[
                                                                                    targetIndex
                                                                                  ]
                                                                                : undefined;

                                                                            if (
                                                                              !targetBlock
                                                                            ) {
                                                                              return "지정 블록";
                                                                            }

                                                                            return `블록 ${targetIndex + 1}`;
                                                                          };
                                                                        const showFromSummary =
                                                                          option.showFromId
                                                                            ? getBranchBlockLabel(
                                                                                option.showFromId
                                                                              )
                                                                            : "기본 흐름";
                                                                        const showToSummary =
                                                                          option.showToId
                                                                            ? getBranchBlockLabel(
                                                                                option.showToId
                                                                              )
                                                                            : "시작 블록";
                                                                        const restartFromSummary =
                                                                          option.restartFromId
                                                                            ? getBranchBlockLabel(
                                                                                option.restartFromId
                                                                              )
                                                                            : "기본 순서";
                                                                        const optionSummary = `${showFromSummary} ~ ${showToSummary} · 이후 ${restartFromSummary}`;

                                                                        return (
                                                                          <Stack
                                                                            key={`${line.id}-button-group-option-${optionIndex}`}
                                                                            spacing={
                                                                              0.5
                                                                            }
                                                                            sx={
                                                                              taskEditorButtonGroupOptionRowSx
                                                                            }
                                                                          >
                                                                            <Chip
                                                                              variant="soft"
                                                                              color="neutral"
                                                                              sx={
                                                                                taskEditorButtonGroupOptionChipSx
                                                                              }
                                                                            >
                                                                              <Stack
                                                                                direction="row"
                                                                                alignItems="center"
                                                                                spacing={
                                                                                  0.5
                                                                                }
                                                                              >
                                                                                <Input
                                                                                  size="sm"
                                                                                  variant="plain"
                                                                                  sx={
                                                                                    taskEditorButtonGroupOptionInputSx
                                                                                  }
                                                                                  placeholder={`옵션 ${
                                                                                    optionIndex +
                                                                                    1
                                                                                  }`}
                                                                                  value={
                                                                                    option.text
                                                                                  }
                                                                                  data-testid={`task-editor-button-group-option-input-${line.id}-${optionIndex}`}
                                                                                  onChange={(
                                                                                    event
                                                                                  ) =>
                                                                                    handleButtonGroupOptionChange(
                                                                                      block.id,
                                                                                      line.id,
                                                                                      optionIndex,
                                                                                      event
                                                                                        .target
                                                                                        .value
                                                                                    )
                                                                                  }
                                                                                  onBlur={
                                                                                    resetTextEditUndoGrouping
                                                                                  }
                                                                                />
                                                                                {line.type ===
                                                                                "Quiz" ? (
                                                                                  <Checkbox
                                                                                    size="sm"
                                                                                    checked={
                                                                                      option.isCorrect ===
                                                                                      true
                                                                                    }
                                                                                    label="정답"
                                                                                    data-testid={`task-editor-quiz-option-correct-${line.id}-${optionIndex}`}
                                                                                    onChange={(
                                                                                      event
                                                                                    ) =>
                                                                                      handleQuizOptionCorrectToggle(
                                                                                        block.id,
                                                                                        line.id,
                                                                                        optionIndex,
                                                                                        event
                                                                                          .target
                                                                                          .checked
                                                                                      )
                                                                                    }
                                                                                  />
                                                                                ) : null}
                                                                                <IconButton
                                                                                  size="sm"
                                                                                  variant="plain"
                                                                                  aria-label={`옵션 ${
                                                                                    optionIndex +
                                                                                    1
                                                                                  } 삭제`}
                                                                                  data-testid={`task-editor-button-group-option-remove-${line.id}-${optionIndex}`}
                                                                                  onClick={() =>
                                                                                    handleRemoveButtonGroupOption(
                                                                                      block.id,
                                                                                      line.id,
                                                                                      optionIndex
                                                                                    )
                                                                                  }
                                                                                  sx={{
                                                                                    minWidth:
                                                                                      "22px",
                                                                                    minHeight:
                                                                                      "22px",
                                                                                    borderRadius:
                                                                                      "999px",
                                                                                  }}
                                                                                >
                                                                                  <span
                                                                                    style={{
                                                                                      fontSize:
                                                                                        "14px",
                                                                                    }}
                                                                                    className="material-symbols-rounded"
                                                                                  >
                                                                                    close
                                                                                  </span>
                                                                                </IconButton>
                                                                              </Stack>
                                                                            </Chip>
                                                                            {line.type ===
                                                                            "ButtonGroup" ? (
                                                                              <>
                                                                                <Box
                                                                                  sx={
                                                                                    taskEditorButtonGroupBranchControlsSx
                                                                                  }
                                                                                >
                                                                                  <Stack
                                                                                    sx={
                                                                                      taskEditorButtonGroupBranchFieldSx
                                                                                    }
                                                                                  >
                                                                                    <Typography
                                                                                      variant="Caption"
                                                                                      sx={
                                                                                        taskEditorButtonGroupBranchFieldLabelSx
                                                                                      }
                                                                                    >
                                                                                      보이기
                                                                                      시작
                                                                                    </Typography>
                                                                                    <Select
                                                                                      size="sm"
                                                                                      value={
                                                                                        option.showFromId ??
                                                                                        ""
                                                                                      }
                                                                                      data-testid={`task-editor-button-group-option-show-from-${line.id}-${optionIndex}`}
                                                                                      onChange={(
                                                                                        _,
                                                                                        value
                                                                                      ) =>
                                                                                        handleButtonGroupOptionBranchChange(
                                                                                          block.id,
                                                                                          line.id,
                                                                                          optionIndex,
                                                                                          "showFromId",
                                                                                          `${value ?? ""}`
                                                                                        )
                                                                                      }
                                                                                      slotProps={{
                                                                                        button:
                                                                                          {
                                                                                            sx: taskEditorButtonGroupBranchSelectSx,
                                                                                          },
                                                                                        listbox:
                                                                                          {
                                                                                            sx: taskEditorSelectListboxSx,
                                                                                          },
                                                                                      }}
                                                                                    >
                                                                                      <Option value="">
                                                                                        기본
                                                                                        흐름
                                                                                      </Option>
                                                                                      {branchTargetBlocks.map(
                                                                                        ({
                                                                                          block:
                                                                                            targetBlock,
                                                                                        }) => (
                                                                                          <Option
                                                                                            key={`${line.id}-button-group-option-show-from-${optionIndex}-${targetBlock.id}`}
                                                                                            value={
                                                                                              targetBlock.id
                                                                                            }
                                                                                          >
                                                                                            {getBranchBlockLabel(
                                                                                              targetBlock.id
                                                                                            )}
                                                                                          </Option>
                                                                                        )
                                                                                      )}
                                                                                    </Select>
                                                                                  </Stack>
                                                                                  <Stack
                                                                                    sx={
                                                                                      taskEditorButtonGroupBranchFieldSx
                                                                                    }
                                                                                  >
                                                                                    <Typography
                                                                                      variant="Caption"
                                                                                      sx={
                                                                                        taskEditorButtonGroupBranchFieldLabelSx
                                                                                      }
                                                                                    >
                                                                                      보이기
                                                                                      종료
                                                                                    </Typography>
                                                                                    <Select
                                                                                      size="sm"
                                                                                      value={
                                                                                        option.showToId ??
                                                                                        ""
                                                                                      }
                                                                                      data-testid={`task-editor-button-group-option-show-to-${line.id}-${optionIndex}`}
                                                                                      onChange={(
                                                                                        _,
                                                                                        value
                                                                                      ) =>
                                                                                        handleButtonGroupOptionBranchChange(
                                                                                          block.id,
                                                                                          line.id,
                                                                                          optionIndex,
                                                                                          "showToId",
                                                                                          `${value ?? ""}`
                                                                                        )
                                                                                      }
                                                                                      slotProps={{
                                                                                        button:
                                                                                          {
                                                                                            sx: taskEditorButtonGroupBranchSelectSx,
                                                                                          },
                                                                                        listbox:
                                                                                          {
                                                                                            sx: taskEditorSelectListboxSx,
                                                                                          },
                                                                                      }}
                                                                                    >
                                                                                      <Option value="">
                                                                                        시작
                                                                                        블록과
                                                                                        동일
                                                                                      </Option>
                                                                                      {branchTargetBlocks.map(
                                                                                        ({
                                                                                          block:
                                                                                            targetBlock,
                                                                                        }) => (
                                                                                          <Option
                                                                                            key={`${line.id}-button-group-option-show-to-${optionIndex}-${targetBlock.id}`}
                                                                                            value={
                                                                                              targetBlock.id
                                                                                            }
                                                                                          >
                                                                                            {getBranchBlockLabel(
                                                                                              targetBlock.id
                                                                                            )}
                                                                                          </Option>
                                                                                        )
                                                                                      )}
                                                                                    </Select>
                                                                                  </Stack>
                                                                                  <Stack
                                                                                    sx={
                                                                                      taskEditorButtonGroupBranchFieldSx
                                                                                    }
                                                                                  >
                                                                                    <Typography
                                                                                      variant="Caption"
                                                                                      sx={
                                                                                        taskEditorButtonGroupBranchFieldLabelSx
                                                                                      }
                                                                                    >
                                                                                      다시
                                                                                      시작
                                                                                    </Typography>
                                                                                    <Select
                                                                                      size="sm"
                                                                                      value={
                                                                                        option.restartFromId ??
                                                                                        ""
                                                                                      }
                                                                                      data-testid={`task-editor-button-group-option-restart-from-${line.id}-${optionIndex}`}
                                                                                      onChange={(
                                                                                        _,
                                                                                        value
                                                                                      ) =>
                                                                                        handleButtonGroupOptionBranchChange(
                                                                                          block.id,
                                                                                          line.id,
                                                                                          optionIndex,
                                                                                          "restartFromId",
                                                                                          `${value ?? ""}`
                                                                                        )
                                                                                      }
                                                                                      slotProps={{
                                                                                        button:
                                                                                          {
                                                                                            sx: taskEditorButtonGroupBranchSelectSx,
                                                                                          },
                                                                                        listbox:
                                                                                          {
                                                                                            sx: taskEditorSelectListboxSx,
                                                                                          },
                                                                                      }}
                                                                                    >
                                                                                      <Option value="">
                                                                                        기본
                                                                                        순서
                                                                                      </Option>
                                                                                      {branchTargetBlocks.map(
                                                                                        ({
                                                                                          block:
                                                                                            targetBlock,
                                                                                        }) => (
                                                                                          <Option
                                                                                            key={`${line.id}-button-group-option-restart-from-${optionIndex}-${targetBlock.id}`}
                                                                                            value={
                                                                                              targetBlock.id
                                                                                            }
                                                                                          >
                                                                                            {getBranchBlockLabel(
                                                                                              targetBlock.id
                                                                                            )}
                                                                                          </Option>
                                                                                        )
                                                                                      )}
                                                                                    </Select>
                                                                                  </Stack>
                                                                                </Box>
                                                                                <Typography
                                                                                  variant="Caption"
                                                                                  sx={
                                                                                    taskEditorButtonGroupSummarySx
                                                                                  }
                                                                                >
                                                                                  {
                                                                                    optionSummary
                                                                                  }
                                                                                </Typography>
                                                                              </>
                                                                            ) : null}
                                                                          </Stack>
                                                                        );
                                                                      }
                                                                    )}
                                                                    {line.type ===
                                                                    "ButtonSelection" ? (
                                                                      <Stack
                                                                        spacing={
                                                                          0.5
                                                                        }
                                                                      >
                                                                        <Checkbox
                                                                          size="sm"
                                                                          checked={
                                                                            line.allowMultipleSelection !==
                                                                            false
                                                                          }
                                                                          label="복수 선택 허용"
                                                                          data-testid={`task-editor-button-selection-allow-multiple-${line.id}`}
                                                                          onChange={(
                                                                            event
                                                                          ) =>
                                                                            handleButtonSelectionAllowMultipleChange(
                                                                              block.id,
                                                                              line.id,
                                                                              event
                                                                                .target
                                                                                .checked
                                                                            )
                                                                          }
                                                                        />
                                                                      </Stack>
                                                                    ) : null}
                                                                    {line.type ===
                                                                    "Quiz" ? (
                                                                      <Stack
                                                                        spacing={
                                                                          0.5
                                                                        }
                                                                      >
                                                                        <Typography
                                                                          variant="Caption"
                                                                          sx={
                                                                            taskEditorCompactFieldLabelSx
                                                                          }
                                                                        >
                                                                          오답
                                                                          문구
                                                                        </Typography>
                                                                        <Input
                                                                          size="sm"
                                                                          placeholder="다시 생각해보세요"
                                                                          value={
                                                                            line.incorrectMessage ??
                                                                            ""
                                                                          }
                                                                          data-testid={`task-editor-quiz-incorrect-message-${line.id}`}
                                                                          onChange={(
                                                                            event
                                                                          ) =>
                                                                            handleQuizIncorrectMessageChange(
                                                                              block.id,
                                                                              line.id,
                                                                              event
                                                                                .target
                                                                                .value
                                                                            )
                                                                          }
                                                                          onBlur={
                                                                            resetTextEditUndoGrouping
                                                                          }
                                                                        />
                                                                      </Stack>
                                                                    ) : null}
                                                                    <Button
                                                                      size="sm"
                                                                      variant="soft"
                                                                      data-testid={`task-editor-button-group-add-option-${line.id}`}
                                                                      onClick={() =>
                                                                        handleAddButtonGroupOption(
                                                                          block.id,
                                                                          line.id
                                                                        )
                                                                      }
                                                                      sx={
                                                                        taskEditorButtonGroupAddButtonSx
                                                                      }
                                                                    >
                                                                      + 옵션
                                                                      추가
                                                                    </Button>
                                                                  </Box>
                                                                </Stack>
                                                              </Box>
                                                            ) : line.type ===
                                                                "Textarea" ||
                                                              line.type ===
                                                                "Preset" ? (
                                                              <Stack
                                                                spacing={0.75}
                                                              >
                                                                <Stack
                                                                  spacing={0.5}
                                                                >
                                                                  <Typography
                                                                    variant="Caption"
                                                                    sx={{
                                                                      color:
                                                                        theme
                                                                          .vars
                                                                          .palette
                                                                          .Content
                                                                          .Weak,
                                                                    }}
                                                                  >
                                                                    {line.type ===
                                                                    "Preset"
                                                                      ? "Preset ID"
                                                                      : "Textarea ID"}
                                                                  </Typography>
                                                                  <Input
                                                                    placeholder="예: reflection1"
                                                                    value={
                                                                      line.content
                                                                    }
                                                                    onChange={(
                                                                      event
                                                                    ) =>
                                                                      handleContentLineCellChange(
                                                                        block.id,
                                                                        line.id,
                                                                        line.id,
                                                                        event
                                                                          .target
                                                                          .value
                                                                      )
                                                                    }
                                                                    onBlur={
                                                                      resetTextEditUndoGrouping
                                                                    }
                                                                  />
                                                                </Stack>
                                                                <Stack
                                                                  spacing={0.5}
                                                                >
                                                                  <Typography
                                                                    variant="Caption"
                                                                    sx={{
                                                                      color:
                                                                        theme
                                                                          .vars
                                                                          .palette
                                                                          .Content
                                                                          .Weak,
                                                                    }}
                                                                  >
                                                                    Placeholder
                                                                  </Typography>
                                                                  <Input
                                                                    placeholder={
                                                                      line.type ===
                                                                      "Preset"
                                                                        ? "예: 기본 문구를 입력하세요"
                                                                        : "예: 소감을 남겨보세요"
                                                                    }
                                                                    value={
                                                                      line.textareaPlaceholder ??
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      event
                                                                    ) =>
                                                                      handleTextareaPlaceholderChange(
                                                                        block.id,
                                                                        line.id,
                                                                        event
                                                                          .target
                                                                          .value
                                                                      )
                                                                    }
                                                                    onBlur={
                                                                      resetTextEditUndoGrouping
                                                                    }
                                                                  />
                                                                </Stack>
                                                              </Stack>
                                                            ) : line.type ===
                                                              "Image" ? (
                                                              <Stack
                                                                spacing={1}
                                                              >
                                                                <Stack
                                                                  spacing={0.5}
                                                                >
                                                                  <Typography
                                                                    variant="Caption"
                                                                    sx={{
                                                                      color:
                                                                        theme
                                                                          .vars
                                                                          .palette
                                                                          .Content
                                                                          .Weak,
                                                                    }}
                                                                  >
                                                                    이미지
                                                                    업로드
                                                                  </Typography>
                                                                  <Button
                                                                    component="label"
                                                                    size="sm"
                                                                    variant="soft"
                                                                    sx={{
                                                                      width:
                                                                        "fit-content",
                                                                    }}
                                                                  >
                                                                    파일 선택
                                                                    <input
                                                                      hidden
                                                                      type="file"
                                                                      accept="image/*"
                                                                      onChange={async (
                                                                        event
                                                                      ) => {
                                                                        const inputElement =
                                                                          event.target as HTMLInputElement;
                                                                        const selectedFile =
                                                                          inputElement
                                                                            .files?.[0];

                                                                        if (
                                                                          !selectedFile
                                                                        ) {
                                                                          return;
                                                                        }

                                                                        await handleImageLineFileChange(
                                                                          block.id,
                                                                          line.id,
                                                                          selectedFile
                                                                        );
                                                                        inputElement.value =
                                                                          "";
                                                                      }}
                                                                    />
                                                                  </Button>
                                                                </Stack>
                                                                <Stack
                                                                  spacing={0.5}
                                                                >
                                                                  <Typography
                                                                    variant="Caption"
                                                                    sx={{
                                                                      color:
                                                                        theme
                                                                          .vars
                                                                          .palette
                                                                          .Content
                                                                          .Weak,
                                                                    }}
                                                                  >
                                                                    이미지 URL /
                                                                    데이터
                                                                  </Typography>
                                                                  <Input
                                                                    placeholder={
                                                                      isTaskEditorLocalImageDataUrl(
                                                                        line.content
                                                                      )
                                                                        ? "로컬 업로드 이미지"
                                                                        : "data:image/... 또는 https://..."
                                                                    }
                                                                    value={
                                                                      isTaskEditorLocalImageDataUrl(
                                                                        line.content
                                                                      )
                                                                        ? "로컬에서 업로드한 이미지 (수정 불가)"
                                                                        : line.content
                                                                    }
                                                                    readOnly={isTaskEditorLocalImageDataUrl(
                                                                      line.content
                                                                    )}
                                                                    disabled={isTaskEditorLocalImageDataUrl(
                                                                      line.content
                                                                    )}
                                                                    onChange={(
                                                                      event
                                                                    ) => {
                                                                      if (
                                                                        isTaskEditorLocalImageDataUrl(
                                                                          line.content
                                                                        )
                                                                      ) {
                                                                        return;
                                                                      }

                                                                      handleContentLineCellChange(
                                                                        block.id,
                                                                        line.id,
                                                                        line.id,
                                                                        event
                                                                          .target
                                                                          .value
                                                                      );
                                                                    }}
                                                                    onBlur={
                                                                      resetTextEditUndoGrouping
                                                                    }
                                                                  />
                                                                </Stack>
                                                                {line.content.trim() && (
                                                                  <Box
                                                                    sx={{
                                                                      width:
                                                                        "100%",
                                                                    }}
                                                                  >
                                                                    <img
                                                                      src={
                                                                        line.content
                                                                      }
                                                                      alt="task-editor-image-preview"
                                                                      style={
                                                                        taskEditorImagePreviewSx as React.CSSProperties
                                                                      }
                                                                    />
                                                                  </Box>
                                                                )}
                                                              </Stack>
                                                            ) : line.type ===
                                                              "AutomaticThought" ? (
                                                              <Stack
                                                                spacing={1}
                                                                data-testid={`task-editor-automatic-thought-editor-${line.id}`}
                                                              >
                                                                <Typography
                                                                  variant="Caption"
                                                                  sx={{
                                                                    color:
                                                                      theme.vars
                                                                        .palette
                                                                        .Content
                                                                        .Weak,
                                                                  }}
                                                                >
                                                                  자동적 사고
                                                                  컴포넌트
                                                                </Typography>
                                                                <Sheet
                                                                  variant="soft"
                                                                  sx={{
                                                                    px: 1.25,
                                                                    py: 1,
                                                                    borderRadius:
                                                                      "10px",
                                                                    border:
                                                                      "1px solid",
                                                                    borderColor:
                                                                      theme.vars
                                                                        .palette
                                                                        .Border
                                                                        .Weak,
                                                                    backgroundColor:
                                                                      theme.vars
                                                                        .palette
                                                                        .Background
                                                                        .Level1,
                                                                  }}
                                                                >
                                                                  <Stack
                                                                    spacing={
                                                                      0.5
                                                                    }
                                                                  >
                                                                    <Typography variant="Body">
                                                                      자동적
                                                                      사고
                                                                      불러오기
                                                                    </Typography>
                                                                  </Stack>
                                                                </Sheet>
                                                              </Stack>
                                                            ) : line.type ===
                                                              "Typography" ? (
                                                              <Stack
                                                                spacing={0.75}
                                                              >
                                                                <Stack
                                                                  spacing={0.5}
                                                                >
                                                                  <Typography
                                                                    variant="Caption"
                                                                    sx={{
                                                                      color:
                                                                        theme
                                                                          .vars
                                                                          .palette
                                                                          .Content
                                                                          .Weak,
                                                                    }}
                                                                  >
                                                                    타입
                                                                  </Typography>
                                                                  <Select<TaskEditorTypographyStyle>
                                                                    size="sm"
                                                                    value={
                                                                      line.typographyStyle ??
                                                                      "Body"
                                                                    }
                                                                    onChange={(
                                                                      _,
                                                                      value
                                                                    ) =>
                                                                      handleTypographyStyleChange(
                                                                        block.id,
                                                                        line.id,
                                                                        value
                                                                      )
                                                                    }
                                                                    sx={{
                                                                      ...taskEditorSelectSx,
                                                                      alignSelf:
                                                                        "flex-start",
                                                                      width:
                                                                        "fit-content",
                                                                      minWidth:
                                                                        {
                                                                          xs: 120,
                                                                          sm: 132,
                                                                        },
                                                                      maxWidth: 180,
                                                                    }}
                                                                    slotProps={{
                                                                      button: {
                                                                        "data-testid": `task-editor-typography-style-select-${line.id}`,
                                                                        sx: taskEditorSelectButtonSx,
                                                                      },
                                                                      listbox: {
                                                                        sx: taskEditorSelectListboxSx,
                                                                      },
                                                                    }}
                                                                  >
                                                                    {TASK_EDITOR_TYPOGRAPHY_STYLES.map(
                                                                      (
                                                                        styleType
                                                                      ) => (
                                                                        <Option
                                                                          key={`${line.id}-typography-style-${styleType}`}
                                                                          value={
                                                                            styleType
                                                                          }
                                                                        >
                                                                          {getTaskEditorTypographyStyleLabel(
                                                                            styleType
                                                                          )}
                                                                        </Option>
                                                                      )
                                                                    )}
                                                                  </Select>
                                                                </Stack>
                                                                <Textarea
                                                                  minRows={2}
                                                                  placeholder="내용을 입력하세요."
                                                                  value={
                                                                    line.content
                                                                  }
                                                                  onChange={(
                                                                    event
                                                                  ) =>
                                                                    handleContentLineCellChange(
                                                                      block.id,
                                                                      line.id,
                                                                      line.id,
                                                                      event
                                                                        .target
                                                                        .value
                                                                    )
                                                                  }
                                                                  onBlur={
                                                                    resetTextEditUndoGrouping
                                                                  }
                                                                />
                                                              </Stack>
                                                            ) : (
                                                              <Textarea
                                                                minRows={2}
                                                                placeholder="내용을 입력하세요."
                                                                value={
                                                                  line.content
                                                                }
                                                                onChange={(
                                                                  event
                                                                ) =>
                                                                  handleContentLineCellChange(
                                                                    block.id,
                                                                    line.id,
                                                                    line.id,
                                                                    event.target
                                                                      .value
                                                                  )
                                                                }
                                                                onBlur={
                                                                  resetTextEditUndoGrouping
                                                                }
                                                              />
                                                            )}
                                                          </Stack>
                                                        </Stack>
                                                      </Sheet>
                                                      <Box
                                                        data-testid="task-editor-line-drop-zone"
                                                        data-drop-preview={
                                                          isAfterPreviewActive
                                                            ? "active"
                                                            : "inactive"
                                                        }
                                                        onDragOver={(event) =>
                                                          handleLineDragOver(
                                                            event,
                                                            {
                                                              blockId: block.id,
                                                              insertionIndex:
                                                                lineIndex + 1,
                                                            }
                                                          )
                                                        }
                                                        onDrop={(event) =>
                                                          handleLineDrop(
                                                            event,
                                                            {
                                                              blockId: block.id,
                                                              insertionIndex:
                                                                lineIndex + 1,
                                                            }
                                                          )
                                                        }
                                                        sx={{
                                                          ...taskEditorLineDropZoneSx,
                                                          height:
                                                            isAfterPreviewActive
                                                              ? "24px"
                                                              : taskEditorLineDropZoneSx.height,
                                                          opacity:
                                                            isLineDragActive
                                                              ? 1
                                                              : 0,
                                                          transform:
                                                            isAfterPreviewActive
                                                              ? "scaleY(1)"
                                                              : "scaleY(0.92)",
                                                        }}
                                                      />
                                                    </React.Fragment>
                                                  );
                                                }
                                              )}
                                            </>
                                          );
                                        })()}
                                      </Stack>
                                    </Stack>
                                  ) : (
                                    <Stack spacing={1}>
                                      <Typography
                                        variant="Caption"
                                        sx={{
                                          color:
                                            theme.vars.palette.Content.Weak,
                                        }}
                                      >
                                        내용
                                      </Typography>
                                      <Textarea
                                        minRows={3}
                                        placeholder="내용을 입력하세요."
                                        value={block.content}
                                        disabled={isTaskEditorLastCardBlock(
                                          block
                                        )}
                                        onChange={(event) =>
                                          handleBlockContentChange(
                                            block.id,
                                            event.target.value
                                          )
                                        }
                                        onBlur={resetTextEditUndoGrouping}
                                      />
                                    </Stack>
                                  )}
                                </Stack>
                              </Sheet>
                              {showAfterDropZone ? (
                                <Stack sx={taskEditorAddBlockButtonWrapSx}>
                                  <IconButton
                                    aria-label="블록 사이에 추가"
                                    data-testid="task-editor-add-block-between"
                                    onClick={() =>
                                      handleAddBlock(
                                        "Instruction",
                                        editableBlockIndex + 1
                                      )
                                    }
                                    sx={taskEditorAddBlockButtonSx}
                                  >
                                    <AddIcon size={16} />
                                  </IconButton>
                                  <Box
                                    data-testid="task-editor-block-drop-zone"
                                    data-drop-preview={
                                      isAfterPreviewActive
                                        ? "active"
                                        : "inactive"
                                    }
                                    onDragOver={(event) =>
                                      handleBlockDragOver(event, {
                                        insertionIndex: editableBlockIndex + 1,
                                      })
                                    }
                                    onDrop={(event) =>
                                      handleBlockDrop(event, {
                                        insertionIndex: editableBlockIndex + 1,
                                      })
                                    }
                                    sx={{
                                      ...taskEditorBlockDropZoneSx,
                                      height: draggedBlockId
                                        ? isAfterPreviewActive
                                          ? "28px"
                                          : taskEditorBlockDropZoneSx.height
                                        : "0px",
                                      opacity: draggedBlockId ? 1 : 0,
                                      transform: isAfterPreviewActive
                                        ? "scaleY(1)"
                                        : "scaleY(0.92)",
                                    }}
                                  />
                                </Stack>
                              ) : null}
                            </React.Fragment>
                          );
                        })()
                      )}
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    ...panelScrollbarSx,
                    px: "20px",
                    py: "16px",
                    backgroundColor: theme.vars.palette.Background.Level1,
                  }}
                  className="scrollbar-hide"
                  data-testid="task-editor-live-preview-panel"
                >
                  <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
                    <TaskEditorLivePreview task={currentEditorState} />
                  </Stack>
                </Box>
              </Box>
            ) : (
              <Box sx={{ px: "20px", py: "16px" }}>
                <Typography
                  variant="Caption"
                  sx={{ color: theme.vars.palette.Content.Weak }}
                >
                  왼쪽 목록에서 활동지를 선택하면 콘텐츠 편집이 열립니다.
                </Typography>
              </Box>
            )}
          </Box>
        </Sheet>
      </Stack>
      <CustomModal
        open={isDeleteModalOpen && Boolean(deletableTaskId)}
        onClose={() => setIsDeleteModalOpen(false)}
        title="활동지를 삭제할까요?"
        subtitle={
          deletableTaskId
            ? `${deletableTaskId} 활동지를 삭제하면 마지막 배포 스냅샷도 함께 제거됩니다.`
            : "삭제할 활동지를 찾을 수 없습니다."
        }
        actions={[
          {
            label: "삭제하기",
            variant: "Solid",
            color: "danger",
            buttonProps: {
              disabled: isDeletingTask,
              loading: isDeletingTask,
            },
            onClick: () => {
              if (!deletableTaskId) {
                setIsDeleteModalOpen(false);
                return;
              }

              handleDeleteSavedTask(deletableTaskId);
            },
          },
          {
            label: "취소",
            variant: "Plain",
            buttonProps: {
              disabled: isDeletingTask,
            },
            onClick: () => setIsDeleteModalOpen(false),
          },
        ]}
      />
      <CustomModal
        open={Boolean(pendingDeleteTarget)}
        onClose={handleCloseDeleteTargetModal}
        title={
          pendingDeleteTargetLabel
            ? `${pendingDeleteTargetLabel}을 삭제할까요?`
            : "삭제할까요?"
        }
        subtitle={
          pendingDeleteTargetLabel
            ? `삭제 대상 type: ${pendingDeleteTargetLabel}`
            : undefined
        }
        actions={[
          {
            label: "삭제하기",
            variant: "Solid",
            color: "danger",
            onClick: handleConfirmDeleteTarget,
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: handleCloseDeleteTargetModal,
          },
        ]}
      />
      <TaskEditorAIGenerateModal
        open={isAIGenerateModalOpen}
        onClose={() => setIsAIGenerateModalOpen(false)}
        onSubmit={(request) => {
          startAIGenerate({
            ...request,
            previousResult: isMeaningfulTaskEditorState(currentEditorState)
              ? currentEditorState
              : undefined,
          });
        }}
      />
      <TaskEditorAIPreviewModal
        open={isAIPreviewModalOpen}
        state={
          aiGenerateStatus.type === "completed" ? aiGenerateStatus.result : null
        }
        onClose={() => setIsAIPreviewModalOpen(false)}
        onApply={handleAIApply}
      />
    </FullPageContainer>
  );
}
